/**
 * Block API Service — calls cogo-cloudflare REST endpoints for block generation,
 * validation, and auto-fix.
 *
 * Usage:
 *   const blockApi = new BlockApiService()
 *   const result = await blockApi.validateBlock(bundle)
 *   const generated = await blockApi.generateBlock({ name: 'hero', type: 'hero', description: '...' })
 *   const fixed = await blockApi.fixBlock(bundle, result.issues)
 */

import { generatePromptFeedbackFromBundle } from '@cogo/validator/browser'
import type { FlowMap } from '@cogo/types-v2'
import type { ApplicationOntologyGenerationBinding } from '@cogo/schema/application-ontology-generation-binding'
import {
  APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN,
  StudioApplicationOntologyGenerationRefusal,
} from '@/features/application-ontology/application-ontology-generation'
import type { AppSpecInteractionContract } from '@cogo/agentic-generator/multiblock/interaction-contract'
import type { DesignBoundApplicationArtifact } from '@cogo/agentic-generator/design-ontology-binding'
import type { LibraryRagOrchestrationEvidence } from '@cogo/agentic-generator/library-rag-agent-orchestration'
import type {
  StudioGenerationEvidence,
  StudioGenerationTargetPlatform,
  StudioRuntimeTargetEvidence,
} from '@/features/validation/studio-generation-evidence'
import type {
  StudioAdaptiveRuntimePlan,
  StudioDecomposePlanningMetadata,
  StudioPlanningSource,
  StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'
import type { FigmaTemplateEnforcementContract } from '@/features/cogo-xyz/figma-template-ontology-matching'
import type { TemplateGenerationModelPolicy } from '@/features/chat/services/template-generation-model-policy'
import type { OntologyMatchingPlan } from '@cogo/headless-core/application-ontology-matching'
import type {
  GeneratedUiVerificationPolicy,
  GeneratedUiVerificationSurface,
  ImageEvidenceSourceKind,
  ImageSourceEvidence,
  PixelRagVisualSegment,
} from '@cogo/headless-core/image-evidence-verification'
import {
  getCogoPlatformBase,
  withCogoDevProxyHeaderRecord,
} from '@/services/cogo-platform-config'
import { getStudioAccessToken } from '@/services/auth-provider'
import {
  parseChatGeneratedApplicationDraftPreparationReceipt,
  type ChatGeneratedApplicationDraftPreparationReceipt,
} from '@/features/chat/services/chat-generated-application-draft-api'
import {
  fingerprintAppGenerationSpec as fingerprintCanonicalAppGenerationSpec,
  isCanonicalAppGenerationSpecRef as isCanonicalAppGenerationSpecReference,
} from './app-generation-spec-identity'
// Terminology selection UI plan (Task 3) — see
// docs/superpowers/plans/terminology-selection-ui-plan.md. The server
// (functions/api/decompose.ts) attaches this to ontologyRetrievalTrace on
// the RAW response; ontology-retrieval-evidence.ts's
// normalizeStudioOntologyRetrievalTrace rebuilds the trace from a fixed
// field whitelist and does NOT carry this field forward, so it is a
// one-time-at-arrival field, not persisted app-spec evidence.
import type { TerminologyDiagnosis } from '../../functions/api/knowledge/terminology-diagnosis'

// ─── Types ────────────────────────────────────────────────────────

export interface BlockBundle {
  block: Record<string, unknown>
  ui: Record<string, unknown>
  events: Record<string, unknown>
  flow: Record<string, unknown>
  variables: Record<string, unknown>
  datasource: Record<string, unknown>
}

/**
 * Wire shape required by POST /api/block/validate and /api/block/fix.
 * The server (cogo-platform `routes/block.ts` BUNDLE_KEYS, canonical since
 * commit 014c30b0) rejects the request with 400 unless `bundle` contains ALL
 * of these keys and each value is an object.
 */
export interface ServerBlockBundle {
  meta: Record<string, unknown>
  ui: Record<string, unknown>
  events: Record<string, unknown>
  flow: Record<string, unknown>
  state: Record<string, unknown>
  data: Record<string, unknown>
}

export interface BlockValidationIssue {
  stage: number
  level: 'error' | 'warning' | 'info'
  message: string
}

export type BlockValidationSource = 'server' | 'local-fallback'

export interface BlockValidateResponse {
  valid: boolean
  score: number
  /**
   * 'partial' is client-only: emitted by the degraded local fallback when its
   * limited checks found no errors — it cannot establish a real pass. The
   * server only ever returns pass/warn/reject.
   */
  grade: 'pass' | 'warn' | 'reject' | 'partial'
  errors: string[]
  warnings: string[]
  issues: BlockValidationIssue[]
  /**
   * Provenance of this result: 'server' = full 14-stage server validation,
   * 'local-fallback' = degraded 3-check local validation. Optional because
   * results embedded in other server responses (fix/generate) predate it;
   * absent means server-produced.
   */
  source?: BlockValidationSource
  /** Why the server call failed — present only when source === 'local-fallback'. */
  fallbackReason?: string
}

export interface BlockSpec {
  name: string
  type: string
  description: string
  binds?: string[]
  specV3?: PromptBlockSpecV3
}

export interface PromptBlockSpecV3 {
  blockType: string
  matchNames: string[]
  ui: {
    requiredElements?: Array<{
      role: string
      context?: string
      matchBy: { text?: string[]; id?: string[]; className?: string[] }
      requiredText?: string
    }>
    requiredDisplayBindings?: Array<{
      role: string
      matchBy: { id: string[] }
      requiredText: string
    }>
  }
  flow: { required: Record<string, unknown> }
  events: { required: Record<string, unknown> }
  state?: Record<string, unknown>
  data?: Record<string, unknown>
}

export interface AppSpecPartial {
  appId?: string
  name?: string
  /** Server-resolved composition entry; never a browser-authored plan. */
  goldenSkuId?: string
  tier?: 'A' | 'B'
  appClass?: 'static' | 'dynamic' | 'hybrid'
  targetPlatform?: 'web' | 'mobile' | 'both' | StudioGenerationTargetPlatform
  generationTargetPlatform?: StudioGenerationTargetPlatform
  runtimeTargets?: StudioRuntimeTargetEvidence[]
  projectPlan?: StudioProjectPlan
  adaptiveRuntime?: StudioAdaptiveRuntimePlan
  planningSource?: StudioPlanningSource
  decomposePlanning?: StudioDecomposePlanningMetadata
  generationEvidence?: StudioGenerationEvidence
  generationResponsibilities?: ApplicationPartDecision[]
  generationQuality?: StudioApplicationGenerationQuality
  ontologyRetrievalTrace?: StudioOntologyRetrievalTrace
  imageEvidenceVerificationEnabled?: boolean
  imageSourceEvidence?: ImageSourceEvidence
  imageEvidenceSource?: StudioImageEvidenceSourceInput
  pixelRagVisualSegments?: PixelRagVisualSegment[]
  generatedUiVerificationSurface?: GeneratedUiVerificationSurface
  generatedUiVerificationSurfaces?: GeneratedUiVerificationSurface[]
  actionFlowTopology?: StudioActionFlowTopologyContract
  actionFlowValidationProof?: StudioActionFlowValidationProof
  actionFlowBindingPlan?: StudioActionFlowBindingPlan
  actionFlowCompiledFlows?: FlowMap
  actionFlowCompilerProof?: StudioActionFlowCompilerProof
  actionFlowRuntimeSimulationProof?: StudioActionFlowRuntimeSimulationProof
  actionFlowReadiness?: StudioActionFlowReadinessEvidence
  applicationKind?: string
  targetAudience?: string
  isValidationHarness?: boolean
  reportApplicationSchemaVersion?: string
  reportApplicationKind?: string
  reportApplicationIntegration?: Record<string, unknown>
  reportExportReadiness?: Record<string, unknown>
  reportReadiness?: Record<string, unknown>
  documentExportReadiness?: Record<string, unknown>
  deterministic?: boolean
  themeHint?: { primaryColor: string; style: string; fontFamily?: string }
  designSystemContract?: Record<string, unknown>
  goldenRecipePack?: StudioGoldenRecipePack
  datasources?: Array<{
    name: string
    type: string
    schema: Record<string, string>
  }>
  globalState?: Record<string, { type: string; default: unknown }>
}

export type GenerationResponsibility =
  | 'binding-engine'
  | 'recipe-compiler'
  | 'llm-semantic'
  | 'blocked'

export type ApplicationPartKind =
  | 'design-system'
  | 'component'
  | 'layout'
  | 'page'
  | 'navigation'
  | 'data-model'
  | 'datasource'
  | 'actionflow'
  | 'auth'
  | 'asset'
  | 'copy'
  | 'business-rule'
  | 'deployment-proof'

export interface ApplicationPartDecision {
  partId: string
  partKind: ApplicationPartKind
  responsibility: GenerationResponsibility
  required: boolean
  ontologyRefs: string[]
  evidenceRefs: string[]
  selectedRecipeRef?: string
  selectedVariantId?: string
  blockedReason?: string
  source?: string
}

export type StudioApplicationAssetQualityStatus =
  | 'packaged'
  | 'reachable'
  | 'loaded'
  | 'missing'
  | 'broken'
  | 'unknown'

export interface StudioImageEvidenceSourceInput {
  sourceId?: string
  sourceKind?: ImageEvidenceSourceKind
  structuredSourceAvailable?: boolean
  sourceDigest?: string
  capturedAt?: string
  captureTool?: ImageSourceEvidence['capture']['tool']
  viewport?: { width: number; height: number }
  deviceClass?: ImageSourceEvidence['capture']['deviceClass']
  unresolvedAmbiguity?: string[]
}

export interface StudioApplicationAssetQualityEvidence {
  assetRef: string
  path?: string
  status: StudioApplicationAssetQualityStatus
  evidenceRef?: string
  source?: string
}

export type StudioApplicationDatasourceQualityStatus =
  | 'seeded'
  | 'live-json'
  | 'mock-json'
  | 'missing'
  | 'html-leak'
  | 'non-json'
  | 'empty-required'
  | 'unknown'

export interface StudioApplicationDatasourceQualityEvidence {
  datasourceName: string
  status: StudioApplicationDatasourceQualityStatus
  responseKind?: 'json' | 'html' | 'text' | 'unknown'
  sampleRowCount?: number
  evidenceRef?: string
  source?: string
  notes?: string[]
}

export interface StudioApplicationGenerationQuality {
  schemaVersion: 'studio-application-generation-quality.v1'
  assets?: {
    required?: boolean
    items: StudioApplicationAssetQualityEvidence[]
  }
  datasources?: {
    required?: boolean
    items: StudioApplicationDatasourceQualityEvidence[]
  }
}

export type StudioOntologyRetrievalMode =
  | 'facet-sparql'
  | 'lexical-sparql'
  | 'ontology-fast-path'
  | 'structural-only'
  | 'vector-rerank'
  | 'vector-unavailable'
  | 'low-similarity-fallback'
  | 'llm-rerank'
  | 'none'

export interface StudioOntologyRetrievalItem {
  assetId?: string
  kind?: string
  sourceRef?: string
  ontologySubject?: string
  chunkId?: string
  score?: number
  vectorSimilarity?: number
  tags?: string[]
  designSystemIds?: string[]
  templateIds?: string[]
  requiredChildIds?: string[]
  optionalChildIds?: string[]
  requiredComponents?: string[]
  actionOutputs?: string[]
  dataInputs?: string[]
  evidenceRefs: string[]
}

export interface StudioActionFlowOntologyPlanGap {
  code: string
  severity: 'error' | 'warning'
  scenarioId?: string
  message: string
  missing: string[]
  recoveryStrategies: string[]
  evidenceRefs: string[]
}

export interface StudioActionFlowOntologyPlanTrace {
  schemaVersion: 'actionflow-ontology-planning-result.v1'
  contractVersion: string
  generatedAt: string
  status: 'matched' | 'partial' | 'gap'
  valid: boolean
  publishBlocked: boolean
  selectedScenarioIds: string[]
  selectedArchetypes: string[]
  selectedOntologySubjects: string[]
  coverage: {
    primitiveKinds: string[]
    stateScopes: string[]
    effectBoundaryKinds: string[]
    stateRelationKinds: string[]
    paramRoles: string[]
    outcomeCriterionKinds: string[]
    readinessStages: string[]
    failureModes: string[]
    recoveryStrategies: string[]
    guardPolicies: string[]
  }
  gaps: StudioActionFlowOntologyPlanGap[]
  evidenceRefs: string[]
}

export interface StudioOntologyRetrievalTrace {
  schemaVersion: 'studio-ontology-retrieval-trace.v1'
  source: string
  generatedAt: string
  mode: StudioOntologyRetrievalMode
  embeddingUsed: boolean
  embeddingModel?: string
  embeddingDimension?: number
  embeddingSimilarityTopK: number
  structuralCandidateCount: number
  vectorCandidateCount: number
  admittedCandidateCount: number
  selectedOntologySubject?: string
  selectedOntologySubjects: string[]
  readbackPassed: boolean
  readbackSubjectIds: string[]
  fallbackUsed: boolean
  retrievalFallbackReason?: string
  vectorOnlyHitRejected: boolean
  lowSimilarityFallback: boolean
  items: StudioOntologyRetrievalItem[]
  actionFlowOntologyPlan?: StudioActionFlowOntologyPlanTrace
  ontologyMatchingPlan?: OntologyMatchingPlan
  ontologyCompilerContract?: StudioOntologyCompilerContract
  imageSourceEvidence?: ImageSourceEvidence
  generatedUiVerificationSurface?: GeneratedUiVerificationSurface
  generatedUiVerificationPolicy?: GeneratedUiVerificationPolicy
  /**
   * Terminology selection UI plan (Task 3) — present only on the RAW
   * decompose response (functions/api/decompose.ts attaches it). Consumed
   * once, at arrival, by app-spec-from-prompt.ts to decide whether to show
   * a terminology-selection chat card; never carried through
   * normalizeStudioOntologyRetrievalTrace into the persisted app spec.
   */
  terminologyDiagnosis?: TerminologyDiagnosis
}

export type StudioOntologyCompilerEntrypoint =
  | 'chat'
  | 'template'
  | 'import'
  | 'simple-edit'
  | 'publish'

export interface StudioOntologyCompilerEntrypointPolicy {
  entrypoint: StudioOntologyCompilerEntrypoint
  enforcement: 'tool-compiler' | 'publish-gate' | 'not-selected'
  evidenceRefs: string[]
}

export interface StudioOntologyCompilerContract {
  schemaVersion: 'studio-ontology-compiler-contract.v1'
  authority: 'tool'
  status: 'ready' | 'blocked'
  selectedDesignSystemId?: string
  selectedTemplateId?: string
  hardConstraints: string[]
  entrypoints: StudioOntologyCompilerEntrypointPolicy[]
  fallbackLadder: Array<{
    from: 'page' | 'ui-block' | 'component'
    to: 'ui-block' | 'component' | 'candidate-queue'
    generationAllowed: boolean
  }>
  actionFlowMutationPolicy: {
    failClosed: true
    requiredValidation: string[]
  }
  llmPolicy: {
    role: 'advisory-only'
    cannotOverrideHardConstraints: true
  }
  candidatePolicy: {
    publishCandidateDirectly: false
    promotionRequires: string[]
  }
  evidenceRefs: string[]
  reasons: string[]
}

export type StudioActionFlowStateScope =
  | 'local'
  | 'appData'
  | 'computed'
  | 'datasource'
  | 'route'
  | 'env'
  | 'props'
  | '_results'

export type StudioActionFlowStateNodeKind =
  | 'local-state'
  | 'global-state'
  | 'computed-projection'
  | 'datasource'
  | 'route-context'
  | 'environment'
  | 'props'
  | 'result'

export interface StudioActionFlowStateNode {
  id: string
  kind: StudioActionFlowStateNodeKind
  scope: StudioActionFlowStateScope
  path: string
  writable: boolean
  ontologyRefs: string[]
  evidenceRefs: string[]
  ownerBlockName?: string
  datasourceName?: string
}

export type StudioActionFlowEffectBoundaryKind =
  | 'pure'
  | 'state'
  | 'network'
  | 'navigation'
  | 'ui'
  | 'auth'
  | 'storage'
  | 'external'
  | 'device'
  | 'media'
  | 'ai'
  | 'crypto'
  | 'realtime'

export interface StudioActionFlowEffectBoundary {
  id: string
  kind: StudioActionFlowEffectBoundaryKind
  sideEffect: boolean
  evidenceRefs: string[]
  requiredCapability?: string
}

export type StudioActionFlowPrimitiveKind =
  | 'set-local-state'
  | 'promote-local-to-global'
  | 'hydrate-local-from-global'
  | 'fetch-datasource-to-global'
  | 'write-global-to-datasource'
  | 'compute-projection'
  | 'navigate'
  | 'guard-by-permission'
  | 'trigger-ui-feedback'
  | 'subscribe-realtime-source'

export type StudioActionFlowCompilerParamRole =
  | 'source'
  | 'target'
  | 'value'
  | 'condition'
  | 'payload'
  | 'route'
  | 'capability'
  | 'adapter-option'
  | 'result'
  | 'error'

export interface StudioActionFlowCompilerParamBinding {
  role: StudioActionFlowCompilerParamRole
  param: string
  sourceNodeId?: string
  targetNodeId?: string
  expression?: string
  resultKey?: string
  value?: unknown
  evidenceRefs: string[]
}

export interface StudioActionFlowPrimitive {
  id: string
  kind: StudioActionFlowPrimitiveKind
  label: string
  required: boolean
  inputNodeIds: string[]
  outputNodeIds: string[]
  effectBoundaryRefs: string[]
  compilerTargetAction?: string
  compilerTargetStepType?: 'conditional'
  compilerParamBindings: StudioActionFlowCompilerParamBinding[]
  ontologyRefs: string[]
  evidenceRefs: string[]
}

export type StudioActionFlowStateRelationKind =
  | 'write'
  | 'promote'
  | 'hydrate'
  | 'cache'
  | 'sync'
  | 'derive'
  | 'read'
  | 'guard'

export interface StudioActionFlowStateRelation {
  id: string
  kind: StudioActionFlowStateRelationKind
  sourceNodeId: string
  targetNodeId: string
  primitiveRef: string
  effectBoundaryRef?: string
  evidenceRefs: string[]
}

export interface StudioActionFlowTopologyContract {
  schemaVersion: 'studio-actionflow-state-topology.v1'
  source: string
  generatedAt: string
  nodes: StudioActionFlowStateNode[]
  relations: StudioActionFlowStateRelation[]
  primitives: StudioActionFlowPrimitive[]
  effectBoundaries: StudioActionFlowEffectBoundary[]
}

export interface StudioActionFlowValidationIssue {
  code: string
  severity: 'error' | 'warning'
  message: string
  nodeId?: string
  primitiveId?: string
  relationId?: string
}

export interface StudioActionFlowValidationProof {
  schemaVersion: 'studio-actionflow-validation-proof.v1'
  valid: boolean
  issues: StudioActionFlowValidationIssue[]
  summary: {
    nodes: number
    relations: number
    primitives: number
    effectBoundaries: number
    requiredPrimitiveKinds: StudioActionFlowPrimitiveKind[]
    coveredPrimitiveKinds: StudioActionFlowPrimitiveKind[]
    missingPrimitiveKinds: StudioActionFlowPrimitiveKind[]
  }
}

export interface StudioActionFlowEventBinding {
  blockName: string
  eventType: string
  source: 'component-event' | 'lifecycle-init' | 'manual'
  evidenceRefs: string[]
}

export interface StudioActionFlowBinding {
  id: string
  primitiveRef: string
  primitiveKind: StudioActionFlowPrimitiveKind
  flowName: string
  trigger: 'event' | 'init' | 'manual'
  event: StudioActionFlowEventBinding
  inputNodeIds: string[]
  outputNodeIds: string[]
  effectBoundaryRefs: string[]
  expectedResultKey?: string
  compilerTargetAction?: string
  compilerTargetStepType?: 'conditional'
  evidenceRefs: string[]
}

export interface StudioActionFlowBindingPlan {
  schemaVersion: 'studio-actionflow-binding-plan.v1'
  source: string
  generatedAt: string
  bindings: StudioActionFlowBinding[]
}

export type StudioActionFlowCompilerIssueCode =
  | 'missing-binding-plan'
  | 'missing-topology'
  | 'missing-primitive-binding'
  | 'binding-missing-primitive'
  | 'binding-missing-source-block'
  | 'binding-cross-block-local-output'
  | 'compiled-flow-missing'
  | 'compiled-flow-empty'
  | 'compiled-flow-invalid-trigger'
  | 'compiled-action-capability-missing'
  | 'compiled-action-generation-forbidden'
  | 'datasource-binding-missing-effect-boundary'

export interface StudioActionFlowCompilerIssue {
  code: StudioActionFlowCompilerIssueCode
  severity: 'error' | 'warning'
  message: string
  bindingId?: string
  primitiveId?: string
  flowName?: string
}

export interface StudioActionFlowCompilerProof {
  schemaVersion: 'studio-actionflow-compiler-proof.v1'
  valid: boolean
  issues: StudioActionFlowCompilerIssue[]
  summary: {
    requiredPrimitives: number
    boundPrimitives: number
    compiledFlows: number
    missingPrimitiveRefs: string[]
  }
}

export interface StudioActionFlowGenerationAuthorityIssue {
  actionName: string
  reason:
    | 'capability-missing'
    | 'inactive'
    | 'not-public'
    | 'runtime-missing'
    | 'generation-forbidden'
    | 'target-unsupported'
  flowNames: string[]
}

export interface StudioActionFlowGenerationAuthority {
  schemaVersion: 'studio-actionflow-generation-authority.v1'
  registrySchemaVersion: string
  generatedAt: string
  targetPlatform: StudioGenerationTargetPlatform
  targetRuntime: 'web' | 'flutter'
  valid: boolean
  referencedActionNames: string[]
  admittedActionNames: string[]
  adapterRequiredActionNames: string[]
  directAtomicActionNames: string[]
  issues: StudioActionFlowGenerationAuthorityIssue[]
  evidenceRefs: string[]
}

export type StudioActionFlowRuntimeSimulationIssueCode =
  | 'missing-compiled-flows'
  | 'binding-flow-missing'
  | 'flow-step-unsupported'
  | 'set-state-target-missing'
  | 'state-write-scope-invalid'
  | 'flow-execution-failed'
  | 'runtime-result-missing'

export interface StudioActionFlowRuntimeSimulationIssue {
  code: StudioActionFlowRuntimeSimulationIssueCode
  severity: 'error' | 'warning'
  message: string
  bindingId?: string
  flowName?: string
  stepName?: string
}

export type StudioActionFlowRuntimeStateScope =
  | 'local'
  | 'appData'
  | 'computed'
  | '_results'
  | 'route'

export interface StudioActionFlowRuntimeStateMutation {
  bindingId: string
  flowName: string
  scope: StudioActionFlowRuntimeStateScope
  path: string
  sourceAction: string
  evidenceRefs: string[]
}

export interface StudioActionFlowRuntimeMockEvidence {
  bindingId: string
  flowName: string
  kind: 'datasource' | 'navigation' | 'auth' | 'ui' | 'realtime' | 'motion'
  target: string
  resultRef?: string
  startedAtMs?: number
  completedAtMs?: number
  durationMs?: number
  evidenceRefs: string[]
}

export interface StudioActionFlowRuntimeLifecycleEvidence {
  bindingId: string
  flowName: string
  kind: 'stage' | 'saga'
  phase:
    | 'onEnter'
    | 'steps'
    | 'onSuccess'
    | 'onError'
    | 'onFinally'
    | 'completed'
    | 'handled'
    | 'failed'
    | 'compensate'
  stageName?: string
  order: number
  clockMs: number
  error?: string
  evidenceRefs: string[]
}

export interface StudioActionFlowRuntimeSimulationProof {
  schemaVersion: 'studio-actionflow-runtime-simulation-proof.v1'
  source: string
  generatedAt: string
  valid: boolean
  issues: StudioActionFlowRuntimeSimulationIssue[]
  stateMutations: StudioActionFlowRuntimeStateMutation[]
  mockEvidence: StudioActionFlowRuntimeMockEvidence[]
  lifecycleEvidence?: StudioActionFlowRuntimeLifecycleEvidence[]
  summary: {
    bindingsSimulated: number
    flowsSimulated: number
    stepsSimulated: number
    stateWrites: number
    resultWrites: number
    datasourceMocks: number
    sideEffectMocks: number
    stagesCompleted?: number
    compensationsAttempted?: number
    motionTimeMs?: number
  }
}

export type StudioActionFlowReadinessStatus =
  | 'ready'
  | 'gap'
  | 'fallback'
  | 'invalid'
  | 'not-required'

export interface StudioActionFlowHeadlessValidationEvidence {
  ok: boolean
  errors: string[]
}

export interface StudioActionFlowHeadlessSimulationEvidence {
  schemaVersion: string
  ok: boolean
  validationErrors: string[]
  errors: string[]
  summary: Record<string, number>
}

export interface StudioActionFlowUserOutcomeEvidence {
  schemaVersion: string
  contractVersion: string
  scenarioId: string
  ok: boolean
  readiness: 'ready' | 'blocked'
  outcomeCount: number
  blockingFailures: string[]
  evidenceRefs: string[]
}

export interface StudioActionFlowBrowserTraceEvidence {
  schemaVersion: string
  contractVersion: string
  scenarioId: string
  ok: boolean
  readiness: 'ready' | 'blocked'
  triggerLabel: string
  evaluationCount: number
  blockingFailures: string[]
  evidenceRefs: string[]
}

export interface StudioActionFlowReadinessPacketEvidence {
  schemaVersion: string
  status: 'ready' | 'blocked'
  publishAllowed: boolean
  stages: Array<{
    id: string
    status: 'ready' | 'blocked' | 'missing'
    blocking: boolean
    summary: string
    failures: string[]
    evidenceRefs: string[]
  }>
  blockingFailures: string[]
  evidenceRefs: string[]
  policy: Record<string, boolean>
}

export interface StudioActionFlowReadinessEvidence {
  schemaVersion: 'studio-actionflow-readiness.v1'
  contractVersion: 'studio-actionflow-state-topology.v1'
  status: StudioActionFlowReadinessStatus
  required: boolean
  generatedAt: string
  evidenceRefs: string[]
  reasons: string[]
  ontologyValidation: {
    ok: boolean
    errors: string[]
  }
  headlessValidation?: StudioActionFlowHeadlessValidationEvidence
  headlessSimulation?: StudioActionFlowHeadlessSimulationEvidence
  userOutcomeProof?: StudioActionFlowUserOutcomeEvidence
  browserTraceProof?: StudioActionFlowBrowserTraceEvidence
  readinessPacket?: StudioActionFlowReadinessPacketEvidence
  summary: {
    topologyValid: boolean
    compilerValid: boolean
    runtimeValid: boolean
    headlessValidationValid?: boolean
    headlessSimulationValid?: boolean
    userOutcomeValid?: boolean
    browserTraceValid?: boolean
    headlessPublishAllowed?: boolean
    primitiveCount: number
    compilerParamBindingCount: number
    runtimeFlowsSimulated: number
    actionFlowOntologyPlanStatus?: 'matched' | 'partial' | 'gap'
    actionFlowOntologyGapCount?: number
  }
}

export type StudioGoldenRecipeScenarioId =
  | 'shopping'
  | 'complex-mobile'
  | 'backoffice-data-table'

export interface StudioGoldenRecipe {
  id: string
  scenarioId: StudioGoldenRecipeScenarioId
  label: string
  requiredBlocks: string[]
  designQualityGates: string[]
  actionFlowContracts: string[]
  stateContracts: string[]
  datasourceContracts: string[]
  browserProof: string[]
}

export interface StudioGoldenRecipePack {
  schemaVersion: 'studio-golden-recipe-pack.v1'
  recipeIds: StudioGoldenRecipeScenarioId[]
  designSystemGates: {
    spacingScalePx: 8
    minTouchTargetPx: 44
    requireSemanticTokens: true
    forbidAdjacentControlsWithoutGap: true
    requireMeasuredDesignEvidence: true
  }
  recipes: StudioGoldenRecipe[]
}

export interface BlockGenerateRequest {
  blockSpec: BlockSpec
  appSpec?: AppSpecPartial
  maxRetries?: number
  /**
   * North-star LLM-free generation. Forwarded verbatim in the request body to
   * the cogo-platform generation endpoint, which must honor it by selecting the
   * top-scored ontology example deterministically (no LLM pick/bind — see
   * `@cogo/agentic-generator` `generateBlock({ deterministic })` /
   * `generateBatch({ deterministic })`). Server support is a cogo-platform
   * follow-up; the client surfaces the flag today.
   */
  deterministic?: boolean
  targetPlatform?: StudioGenerationTargetPlatform
  /**
   * Same field as ProjectMeta / POST /api/decompose. Optional. JSON.stringify
   * drops undefined, so a request without a declared project scope stays
   * byte-identical. Do not send `styleTarget` — the generate consumer maps
   * `app:wonbanking` only.
   */
  terminologyScope?: string
}

export interface BlockGenerateResponse {
  ok: boolean
  block?: Record<string, unknown>
  qualityScore?: number
  validationResult?: BlockValidateResponse
  metrics?: { durationMs: number; retryCount: number }
  error?: string
}

export interface BlockGenerateV2Response {
  ok: boolean
  block?: Record<string, unknown>
  qualityScore?: number
  validationResult?: BlockValidateResponse
  bddResult?: Record<string, unknown> | null
  metrics?: { durationMs: number; pipeline: string; fixed: boolean }
  error?: string
}

export function isBlockGenerateV2Accepted(
  result: Pick<BlockGenerateV2Response, 'ok' | 'block' | 'error' | 'validationResult'>,
): { accepted: true } | { accepted: false; error: string } {
  if (!result.ok || !result.block) {
    return { accepted: false, error: result.error ?? 'Generation failed' }
  }
  const validation = result.validationResult
  if (!validation) return { accepted: true }
  const errors = Array.isArray(validation.errors)
    ? validation.errors.filter((item) => typeof item === 'string' && item.length > 0)
    : []
  const issueErrors = Array.isArray(validation.issues)
    ? validation.issues.filter((issue) => issue.level === 'error')
    : []
  if (validation.valid === false && (errors.length > 0 || issueErrors.length > 0)) {
    return {
      accepted: false,
      error: errors[0] ?? issueErrors[0]?.message ?? 'Generation validation failed',
    }
  }
  return { accepted: true }
}

export interface BlockFixResponse {
  ok: boolean
  fixedBundle?: BlockBundle
  fixSummary?: { applied: number; skipped: number; details: string[] }
  newScore?: number
  newValidation?: BlockValidateResponse
  error?: string
}

export interface AppSpecFull {
  appId?: string
  projectId?: string
  name: string
  /** Server-resolved composition entry; never a browser-authored plan. */
  goldenSkuId?: string
  /**
   * Human-confirmed intake-card decisions (WonBanking S1-S4/S7 lane) that
   * constrained this plan. Structured — parsed from the intake cards' blocks
   * by the shared grammar in @cogo/agentic-generator, echoed back by
   * cogo-decompose-api when it consumed them, and persisted at project birth
   * as `settings.intakeDecisions`, so downstream reads data, never prose.
   */
  intakeDecisions?: import('@cogo/agentic-generator').IntakeDecisions
  tier?: 'A' | 'B'
  appClass?: 'static' | 'dynamic' | 'hybrid'
  description?: string
  targetPlatform?: 'web' | 'mobile' | 'both' | StudioGenerationTargetPlatform
  generationTargetPlatform?: StudioGenerationTargetPlatform
  runtimeTargets?: StudioRuntimeTargetEvidence[]
  projectPlan?: StudioProjectPlan
  /** Exact fail-closed Library + Design Ontology compilation receipt. */
  designBoundApplicationArtifact?: DesignBoundApplicationArtifact
  /** Optional persisted projection of a successful Library RAG receipt. */
  libraryRagOrchestrationEvidence?: LibraryRagOrchestrationEvidence
  /** Exact render contract compiled from the bound Design Ontology release. */
  applicationCompositionPlan?: DesignBoundApplicationArtifact['design']
  adaptiveRuntime?: StudioAdaptiveRuntimePlan
  planningSource?: StudioPlanningSource
  decomposePlanning?: StudioDecomposePlanningMetadata
  generationEvidence?: StudioGenerationEvidence
  generationResponsibilities?: ApplicationPartDecision[]
  generationQuality?: StudioApplicationGenerationQuality
  templateEnforcement?: FigmaTemplateEnforcementContract
  templateGenerationModelPolicy?: TemplateGenerationModelPolicy
  ontologyRetrievalTrace?: StudioOntologyRetrievalTrace
  imageEvidenceVerificationEnabled?: boolean
  imageSourceEvidence?: ImageSourceEvidence
  imageEvidenceSource?: StudioImageEvidenceSourceInput
  pixelRagVisualSegments?: PixelRagVisualSegment[]
  generatedUiVerificationSurface?: GeneratedUiVerificationSurface
  generatedUiVerificationSurfaces?: GeneratedUiVerificationSurface[]
  actionFlowTopology?: StudioActionFlowTopologyContract
  actionFlowValidationProof?: StudioActionFlowValidationProof
  actionFlowBindingPlan?: StudioActionFlowBindingPlan
  actionFlowCompiledFlows?: FlowMap
  actionFlowCompilerProof?: StudioActionFlowCompilerProof
  actionFlowGenerationAuthority?: StudioActionFlowGenerationAuthority
  actionFlowRuntimeSimulationProof?: StudioActionFlowRuntimeSimulationProof
  actionFlowReadiness?: StudioActionFlowReadinessEvidence
  applicationKind?: string
  targetAudience?: string
  isValidationHarness?: boolean
  reportApplicationSchemaVersion?: string
  reportApplicationKind?: string
  reportApplicationIntegration?: Record<string, unknown>
  reportExportReadiness?: Record<string, unknown>
  reportReadiness?: Record<string, unknown>
  documentExportReadiness?: Record<string, unknown>
  deterministic?: boolean
  pages?: Array<{ id: string; route: string; title: string; blocks: string[] }>
  blocks: Array<{
    name: string
    type: string
    description: string
    binds?: string[]
    specV3?: PromptBlockSpecV3
  }>
  datasources?: Array<{
    name: string
    type: string
    schema: Record<string, string>
  }>
  globalState?: Record<string, { type: string; default: unknown }>
  interactionContract?: AppSpecInteractionContract
  themeHint?: { primaryColor: string; style: string; fontFamily?: string }
  /** Exact six-axis Design System authority selected by the server release. */
  generationDesignSystem?: import('@cogo/design-data').DesignSystemGenerationContract
  designSystemTokens?: Record<string, string>
  designSystemContract?: Record<string, unknown>
  /**
   * Flat CSS-variable map the GENERATOR consumes. Distinct from
   * `designSystemTokens`, which only ever reaches the local renderer: measured
   * 2026-07-30, the platform's block prompt reads `designSystem`
   * (cogo-cloudflare lib/block-generator-v2.ts:1249, intake routes/
   * app-v2.ts:1262) and the durable job lane's allowlist keeps `designSystem`
   * while discarding `designSystemTokens`. Set today only by a source-bound
   * design system binding; the preset/pack paths leave it undefined, so the
   * request body for those generations is byte-identical to before.
   */
  designSystem?: Record<string, string>
  goldenRecipePack?: StudioGoldenRecipePack
  designContract?: Record<string, string>
}

export interface AppValidateResponse {
  scores: Record<
    string,
    { score: number; grade: string; errors: string[]; warnings: string[] }
  >
  crossBlock: { issues: string[]; issueCount: number }
  averageScore: number
  overallGrade: 'pass' | 'warn' | 'reject'
  blockCount: number
}

export interface AppGenerateEvent {
  event: string
  data: Record<string, unknown>
}

export type AppGenerationJobStatus =
  | 'creating'
  | 'queued'
  | 'running'
  | 'complete'
  | 'failed'
  | 'cancelled'

export interface AppGenerationJobDescriptor {
  schemaVersion: 'studio-app-generation-job-descriptor.v1'
  requestId: string
  jobId?: string
  projectId: string
  appId: string
  status: 'creating' | 'running' | 'lifecycle-pending'
  /**
   * The Platform explicitly declined the durable-job lane, so this exact
   * creating subject is continuing through SSE. Refresh may verify the
   * persisted project, but must never replay generation as a background job.
   */
  executionLane?: 'direct-fallback'
  statusUrl?: string
  resumeToken?: string
  /** Expiry of the server-issued resume capability. */
  tokenExpiresAt?: string
  /** Server-owned terminal deadline; startedAt remains UI telemetry only. */
  terminalDeadlineAt?: string
  /** Exact server-owned workflow subject fingerprint returned at create. */
  serverAppFingerprint?: string
  /**
   * Canonical pre-enrichment AppSpec authority persisted by Studio. This is
   * intentionally independent from appSpecFingerprint, which identifies the
   * enriched Platform request subject used for reconnect.
   */
  generationAuthorityAppSpecRef?: string
  /** Exact bounded Chat reservation proof required after a hard refresh. */
  chatGeneratedApplicationDraftPreparation?: ChatGeneratedApplicationDraftPreparationReceipt
  appSpecFingerprint: string
  /**
   * Sanitized, bounded request subject used only to re-enter the existing
   * Studio completion pipeline after a browser refresh. It never contains a
   * Studio JWT or the server-only bounded review policy.
   */
  appSpec: AppSpecFull
  startedAt: number
  terminalSloMs?: number
}

export const APP_GENERATION_PERSISTENCE_SUBJECT_SETTINGS_KEY =
  'cogoAppGenerationSubject' as const

export interface AppGenerationPersistenceSubject {
  schemaVersion: 'studio-app-generation-persistence-subject.v1'
  projectId: string
  appId: string
  appSpecFingerprint: string
}

export function createAppGenerationPersistenceSubject(
  descriptor: AppGenerationJobDescriptor,
): AppGenerationPersistenceSubject {
  return {
    schemaVersion: 'studio-app-generation-persistence-subject.v1',
    projectId: descriptor.projectId,
    appId: descriptor.appId,
    appSpecFingerprint: descriptor.appSpecFingerprint,
  }
}

export function readAppGenerationPersistenceSubject(
  settingsValue: unknown,
): AppGenerationPersistenceSubject | null {
  if (
    !settingsValue ||
    typeof settingsValue !== 'object' ||
    Array.isArray(settingsValue)
  ) {
    return null
  }
  const value = (settingsValue as Record<string, unknown>)[
    APP_GENERATION_PERSISTENCE_SUBJECT_SETTINGS_KEY
  ]
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  if (
    record.schemaVersion !==
      'studio-app-generation-persistence-subject.v1' ||
    typeof record.projectId !== 'string' ||
    !record.projectId ||
    typeof record.appId !== 'string' ||
    !record.appId ||
    typeof record.appSpecFingerprint !== 'string' ||
    !/^studio-app-spec-sha256-v1-[0-9a-f]{64}$/.test(
      record.appSpecFingerprint,
    )
  ) {
    return null
  }
  return record as unknown as AppGenerationPersistenceSubject
}

export interface AppGenerationBackgroundJobProgress {
  schemaVersion: 'studio-app-generation-background-progress.v1'
  projectId: string
  jobId?: string
  status: 'queued' | 'generating' | 'reconnecting' | 'paused'
  stage?: string
  canResume: boolean
  continuesInBackground: true
  terminalSloMs?: number
}

export type AgenticActionFlowTestChunkCandidateMode = 'kg-seed' | 'env' | 'llm'

export interface AgenticActionFlowTestChunkProofRequest {
  fixtures: unknown[]
  renderablesByCaseId: Record<string, unknown>
  candidateMode?: AgenticActionFlowTestChunkCandidateMode
  promote?: boolean
  promotion?: {
    enabled?: boolean
    syncMode?: 'best-effort' | 'durable'
    sessionId?: string
  }
}

export interface AgenticActionFlowTestChunkProofResponse {
  schemaVersion: 'cogo.agentic-actionflow-test-chunk-route-response.v1'
  status: 'pass' | 'blocked'
  candidateMode: AgenticActionFlowTestChunkCandidateMode
  provider?: {
    status?: string
    providerName?: string
    model?: string
  }
  proof?: Record<string, unknown>
  promotionRecord?: Record<string, unknown>
  blockedReasons: string[]
}

// ─── Wire mapping ─────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * The server's flow stages (cogo-platform `lib/block-validator.ts`
 * `collectFlowNames`/`validateFlowSteps`) read `flow.flows` — the v4
 * flow.cogo.json FILE shape. A flat FlowMap (flowName → Flow, the types-v2 /
 * template-loader shape) is invisible to them: every event→flow reference
 * silently passes and step validation never runs. Wrap flat maps.
 */
function toServerFlow(flow: Record<string, unknown>): Record<string, unknown> {
  if (flow.flows && typeof flow.flows === 'object') return flow
  return Object.keys(flow).length > 0 ? { flows: flow } : flow
}

/**
 * The server's event stages (`validateCrossReferences`/
 * `collectBoundEventElementIds`) read `events.bindings` ("elementId:eventName"
 * keys, v4 events.cogo.json) or `events.handlers`. The flat types-v2 EventMap
 * (elementId → eventName → binding) used by editor-created blocks reads as
 * "completely empty" — a stage-12 false reject on healthy interactive blocks.
 * Convert EventMap to the bindings shape; pass anything else through.
 */
function toServerEvents(
  events: Record<string, unknown>,
): Record<string, unknown> {
  if ('bindings' in events || 'handlers' in events) return events
  const entries = Object.entries(events)
  if (entries.length === 0) return events
  const bindings: Record<string, unknown> = {}
  for (const [elementId, byEvent] of entries) {
    if (!byEvent || typeof byEvent !== 'object' || Array.isArray(byEvent))
      return events
    for (const [eventName, binding] of Object.entries(
      byEvent as Record<string, unknown>,
    )) {
      bindings[`${elementId}:${eventName}`] = binding
    }
  }
  return { bindings }
}

/**
 * Map a client bundle to the server wire contract ({@link ServerBlockBundle}).
 * Accepts both the studio v1 shape ({block, ui, events, flow, variables,
 * datasource}) and objects already in canonical v2/server shape ({meta, ui,
 * events, flow, state, data}) — several call sites cast generate-API responses
 * (which are canonical-shaped) to `BlockBundle`, so both must survive.
 * flow/events are additionally mapped to the inner shapes the server stages
 * actually read (see {@link toServerFlow} / {@link toServerEvents}).
 */
export function toServerBlockBundle(bundle: BlockBundle): ServerBlockBundle {
  const b = bundle as unknown as Record<string, unknown>
  return {
    meta: asRecord(b.meta ?? b.block),
    ui: asRecord(b.ui),
    events: toServerEvents(asRecord(b.events)),
    flow: toServerFlow(asRecord(b.flow)),
    state: asRecord(b.state ?? b.variables),
    data: asRecord(b.data ?? b.datasource),
  }
}

/** Inverse of {@link toServerEvents}: server bindings → types-v2 EventMap. */
function fromServerEvents(
  events: Record<string, unknown>,
): Record<string, unknown> {
  const bindings = events.bindings
  if (!bindings || typeof bindings !== 'object' || Array.isArray(bindings))
    return events
  const map: Record<string, Record<string, unknown>> = {}
  for (const [key, binding] of Object.entries(
    bindings as Record<string, unknown>,
  )) {
    const idx = key.indexOf(':')
    if (idx <= 0) return events
    ;(map[key.slice(0, idx)] ??= {})[key.slice(idx + 1)] = binding
  }
  return map
}

/**
 * Map a server-canonical bundle ({meta, ui, events, flow, state, data} — the
 * ONLY shape a successful /api/block/fix can return, since the server re-checks
 * the LLM output against BUNDLE_KEYS) back to the studio v1 {@link BlockBundle}
 * every consumer expects (toV2Bundle, insertBlockIntoProject, evidence-meta
 * merge all read .block/.variables/.datasource). Without this, a successful
 * fix silently drops the fixed meta/state/data before being written into the
 * user's project. Tolerates v1-shaped input (passthrough).
 */
export function fromServerBlockBundle(
  bundle: Record<string, unknown>,
): BlockBundle {
  const flow = asRecord(bundle.flow)
  return {
    block: asRecord(bundle.block ?? bundle.meta),
    ui: asRecord(bundle.ui),
    events: fromServerEvents(asRecord(bundle.events)),
    flow:
      flow.flows && typeof flow.flows === 'object'
        ? asRecord(flow.flows)
        : flow,
    variables: asRecord(bundle.variables ?? bundle.state),
    datasource: asRecord(bundle.datasource ?? bundle.data),
  }
}

// ─── Service ──────────────────────────────────────────────────────

const DEFAULT_BASE_URL = getCogoPlatformBase()

/**
 * Studio's full-application generation contract always asks the server for two
 * independent, bounded reviews. Gemma reviews structural/evidence facts only
 * on this pre-render server path; actual pixel judgment is reserved for the
 * screenshot E2E lane. Neither model may become the final Cogo JSON writer.
 * Keep this policy fixed here instead of accepting a browser-provided role,
 * mode, model identity, or budget.
 */
const REQUIRED_APPLICATION_MODEL_REVIEW = Object.freeze({
  enabled: true,
  mode: 'required' as const,
  roles: [
    'qwen-ontology-reviewer',
    'gemma-structural-evidence-reviewer',
  ] as const,
  timeoutMs: 30_000,
  maxTokens: 512,
  numPredict: 512,
  requireServedModelMatch: true,
})

const APP_GENERATION_JOB_DESCRIPTOR_VERSION =
  'studio-app-generation-job-descriptor.v1' as const
const APP_GENERATION_JOB_STORAGE_PREFIX =
  'coogo.studio.app-generation-job.v1:'
const APP_GENERATION_JOB_ACTIVE_POINTER_KEY =
  'coogo.studio.app-generation-active-pointer.v1'
const APP_GENERATION_JOB_ACTIVE_POINTER_VERSION =
  'studio-app-generation-active-pointer.v1' as const
const APP_GENERATION_JOB_MAX_DESCRIPTOR_BYTES = 750_000
const APP_GENERATION_JOB_MAX_DISCOVERY_KEYS = 8
const APP_GENERATION_JOB_MAX_POINTER_AGE_MS = 24 * 60 * 60 * 1_000
const APP_GENERATION_JOB_MAX_CREATING_AGE_MS = 15 * 60 * 1_000
const APP_GENERATION_JOB_MIN_POLL_MS = 1_500
const APP_GENERATION_JOB_MAX_POLL_MS = 3_000
const APP_GENERATION_JOB_STATUS_READ_TIMEOUT_MS = 10_000

const appGenerationJobDescriptorMemory = new Map<
  string,
  AppGenerationJobDescriptor
>()
const appGenerationJobResumeControls = new Map<string, () => void>()

interface AppGenerationJobActivePointer {
  schemaVersion: typeof APP_GENERATION_JOB_ACTIVE_POINTER_VERSION
  projectId: string
  appId: string
  requestId: string
  appSpecFingerprint: string
  updatedAt: number
}

function appGenerationJobStorageKey(projectId: string): string {
  return `${APP_GENERATION_JOB_STORAGE_PREFIX}${encodeURIComponent(projectId)}`
}

function browserStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

function descriptorMatchesActivePointer(
  descriptor: AppGenerationJobDescriptor,
  pointer: AppGenerationJobActivePointer,
): boolean {
  return (
    descriptor.projectId === pointer.projectId &&
    descriptor.appId === pointer.appId &&
    descriptor.requestId === pointer.requestId &&
    descriptor.appSpecFingerprint === pointer.appSpecFingerprint
  )
}

function parseAppGenerationJobActivePointer(
  value: unknown,
): AppGenerationJobActivePointer | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  if (
    record.schemaVersion !== APP_GENERATION_JOB_ACTIVE_POINTER_VERSION ||
    typeof record.projectId !== 'string' ||
    !record.projectId ||
    typeof record.appId !== 'string' ||
    !record.appId ||
    typeof record.requestId !== 'string' ||
    !record.requestId ||
    typeof record.appSpecFingerprint !== 'string' ||
    !record.appSpecFingerprint ||
    typeof record.updatedAt !== 'number' ||
    !Number.isFinite(record.updatedAt)
  ) {
    return null
  }
  return record as unknown as AppGenerationJobActivePointer
}

function readAppGenerationJobActivePointer(
  storage: Storage,
): AppGenerationJobActivePointer | null {
  try {
    const encoded = storage.getItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY)
    if (!encoded) return null
    const pointer = parseAppGenerationJobActivePointer(JSON.parse(encoded))
    if (!pointer) storage.removeItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY)
    return pointer
  } catch {
    storage.removeItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY)
    return null
  }
}

function writeAppGenerationJobActivePointer(
  storage: Storage,
  descriptor: AppGenerationJobDescriptor,
): void {
  const pointer: AppGenerationJobActivePointer = {
    schemaVersion: APP_GENERATION_JOB_ACTIVE_POINTER_VERSION,
    projectId: descriptor.projectId,
    appId: descriptor.appId,
    requestId: descriptor.requestId,
    appSpecFingerprint: descriptor.appSpecFingerprint,
    updatedAt: Date.now(),
  }
  storage.setItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY, JSON.stringify(pointer))
}

/** Canonical AppSpec authority identity; cache and resume reuse the same digest. */
export function fingerprintAppGenerationSpec(appSpec: AppSpecFull): string {
  return fingerprintCanonicalAppGenerationSpec(appSpec)
}

export function isCanonicalAppGenerationSpecRef(
  value: unknown,
): value is string {
  return isCanonicalAppGenerationSpecReference(value)
}

function isAppGenerationFingerprint(value: unknown): value is string {
  return isCanonicalAppGenerationSpecRef(value)
}

function requestedGenerationAuthorityRefMatches(
  stored: AppGenerationJobDescriptor,
  requested: string | undefined,
): boolean {
  return requested === undefined ||
    stored.generationAuthorityAppSpecRef === requested
}

function sanitizeAppSpecForGenerationJob(
  appSpec: AppSpecFull,
): AppSpecFull | null {
  try {
    const clone = JSON.parse(JSON.stringify(appSpec)) as AppSpecFull & {
      boundedModelReview?: unknown
    }
    delete clone.boundedModelReview
    return clone
  } catch {
    return null
  }
}

function hasApplicationCompositionPlan(appSpec: AppSpecFull): boolean {
  const record = appSpec as unknown as Record<string, unknown>
  const generationEvidence = asRecord(record.generationEvidence)
  return Boolean(
    record.applicationCompositionPlan ||
      generationEvidence.applicationCompositionPlan,
  )
}

function hasGoldenSkuSelection(appSpec: AppSpecFull): boolean {
  return Boolean(appSpec.goldenSkuId?.trim())
}

/**
 * Generic Application Ontology Program — post-program wiring lane A1, seam 1.
 *
 * Throws the program's OWN operational refusal code (imported, never
 * re-spelled) when a bound generation is about to run on a lane that cannot
 * carry its binding. An unbound generation passes straight through, so the
 * ordinary lane is byte-for-byte unchanged.
 */
function assertNoApplicationOntologyGenerationFallback(
  applicationOntologyGenerationBinding: ApplicationOntologyGenerationBinding | undefined,
): void {
  if (!applicationOntologyGenerationBinding) return
  throw new StudioApplicationOntologyGenerationRefusal(
    APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN,
  )
}

/**
 * Template/receipt-bound, deterministic ontology, and explicit composition
 * lanes retain their existing SSE semantics. Only the ordinary app lane is
 * eligible for the durable Platform job boundary.
 */
export function shouldUseAppGenerationJob(appSpec: AppSpecFull): boolean {
  return !(
    appSpec.templateEnforcement ||
    appSpec.templateGenerationModelPolicy ||
    appSpec.deterministic === true ||
    hasGoldenSkuSelection(appSpec) ||
    hasApplicationCompositionPlan(appSpec)
  )
}

function normalizeJobStatusUrl(baseUrl: string, statusUrl: string): string {
  const base = new URL(baseUrl)
  const resolved = new URL(statusUrl, `${base.origin}/`)
  if (resolved.origin !== base.origin) {
    throw new Error(
      'App generation job status URL must use the configured Platform origin.',
    )
  }
  return resolved.toString()
}

function parseStoredAppGenerationJobDescriptor(
  value: unknown,
  projectId: string,
  baseUrl: string,
): AppGenerationJobDescriptor | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  if (
    record.schemaVersion !== APP_GENERATION_JOB_DESCRIPTOR_VERSION ||
    record.projectId !== projectId ||
    typeof record.requestId !== 'string' ||
    !record.requestId ||
    typeof record.appId !== 'string' ||
    !record.appId ||
    !['creating', 'running', 'lifecycle-pending'].includes(
      String(record.status),
    ) ||
    typeof record.appSpecFingerprint !== 'string' ||
    typeof record.startedAt !== 'number' ||
    !Number.isFinite(record.startedAt) ||
    !record.appSpec ||
    typeof record.appSpec !== 'object' ||
    Array.isArray(record.appSpec)
  ) {
    return null
  }
  if (record.chatGeneratedApplicationDraftPreparation !== undefined) {
    try {
      const encoded = JSON.stringify(record.chatGeneratedApplicationDraftPreparation)
      if (
        encoded.length > 12_288 ||
        parseChatGeneratedApplicationDraftPreparationReceipt(
          record.chatGeneratedApplicationDraftPreparation,
        ).subject.projectId !== projectId
      ) {
        return null
      }
    } catch {
      return null
    }
  }
  if (
    record.generationAuthorityAppSpecRef !== undefined &&
    !isAppGenerationFingerprint(record.generationAuthorityAppSpecRef)
  ) {
    return null
  }
  if (
    record.executionLane !== undefined &&
    record.executionLane !== 'direct-fallback'
  ) {
    return null
  }
  if (
    record.executionLane === 'direct-fallback' &&
    (record.status !== 'creating' ||
      record.jobId !== undefined ||
      record.statusUrl !== undefined ||
      record.resumeToken !== undefined ||
      record.serverAppFingerprint !== undefined)
  ) {
    return null
  }
  if (record.status === 'running') {
    if (
      typeof record.jobId !== 'string' ||
      !record.jobId ||
      typeof record.statusUrl !== 'string' ||
      !record.statusUrl ||
      typeof record.resumeToken !== 'string' ||
      !record.resumeToken ||
      typeof record.tokenExpiresAt !== 'string' ||
      !Number.isFinite(Date.parse(record.tokenExpiresAt)) ||
      Date.parse(record.tokenExpiresAt) <= Date.now() ||
      typeof record.terminalDeadlineAt !== 'string' ||
      !Number.isFinite(Date.parse(record.terminalDeadlineAt)) ||
      typeof record.serverAppFingerprint !== 'string' ||
      !record.serverAppFingerprint
    ) {
      return null
    }
    try {
      normalizeJobStatusUrl(baseUrl, record.statusUrl)
    } catch {
      return null
    }
  } else if (record.status === 'lifecycle-pending') {
    const hasServerJob =
      typeof record.jobId === 'string' && Boolean(record.jobId)
    const hasServerFingerprint =
      typeof record.serverAppFingerprint === 'string' &&
      Boolean(record.serverAppFingerprint)
    // A Platform background job carries both server identities. A direct SSE
    // generation has neither, but still needs the same durable browser pointer
    // while Studio finishes the server-authoritative candidate lifecycle.
    if (hasServerJob !== hasServerFingerprint) return null
  }
  const appSpec = record.appSpec as AppSpecFull
  if (
    appSpec.projectId !== projectId ||
    appSpec.appId !== record.appId ||
    fingerprintAppGenerationSpec(appSpec) !== record.appSpecFingerprint
  ) {
    return null
  }
  return record as unknown as AppGenerationJobDescriptor
}

function readStoredAppGenerationJobCandidates(
  storage: Storage,
  baseUrl: string,
): AppGenerationJobDescriptor[] | null {
  const keys: string[] = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key?.startsWith(APP_GENERATION_JOB_STORAGE_PREFIX)) continue
    keys.push(key)
    if (keys.length > APP_GENERATION_JOB_MAX_DISCOVERY_KEYS) return null
  }

  const descriptors: AppGenerationJobDescriptor[] = []
  for (const key of keys) {
    const encodedProjectId = key.slice(APP_GENERATION_JOB_STORAGE_PREFIX.length)
    let projectId: string
    try {
      projectId = decodeURIComponent(encodedProjectId)
    } catch {
      storage.removeItem(key)
      continue
    }
    if (!projectId || appGenerationJobStorageKey(projectId) !== key) {
      storage.removeItem(key)
      continue
    }
    try {
      const encoded = storage.getItem(key)
      if (!encoded) continue
      const descriptor = parseStoredAppGenerationJobDescriptor(
        JSON.parse(encoded),
        projectId,
        baseUrl,
      )
      if (!descriptor) {
        storage.removeItem(key)
        appGenerationJobDescriptorMemory.delete(projectId)
        continue
      }
      descriptors.push(descriptor)
      appGenerationJobDescriptorMemory.set(projectId, descriptor)
    } catch {
      storage.removeItem(key)
      appGenerationJobDescriptorMemory.delete(projectId)
    }
  }
  return descriptors
}

function activePointerIsFresh(
  pointer: AppGenerationJobActivePointer,
  descriptor: AppGenerationJobDescriptor,
): boolean {
  const now = Date.now()
  if (
    pointer.updatedAt > now + 60_000 ||
    now - pointer.updatedAt > APP_GENERATION_JOB_MAX_POINTER_AGE_MS ||
    descriptor.startedAt > now + 60_000
  ) {
    return false
  }
  if (descriptor.status === 'creating') {
    return now - descriptor.startedAt <= APP_GENERATION_JOB_MAX_CREATING_AGE_MS
  }
  if (descriptor.status === 'running') {
    const terminalDeadline = Date.parse(descriptor.terminalDeadlineAt ?? '')
    return Number.isFinite(terminalDeadline) && terminalDeadline > now
  }
  return true
}

function persistAppGenerationJobDescriptor(
  descriptor: AppGenerationJobDescriptor,
): boolean {
  const storage = browserStorage()
  if (!storage) return false
  try {
    const encoded = JSON.stringify(descriptor)
    if (new Blob([encoded]).size > APP_GENERATION_JOB_MAX_DESCRIPTOR_BYTES) {
      return false
    }
    const storedDescriptors = readStoredAppGenerationJobCandidates(
      storage,
      DEFAULT_BASE_URL,
    )
    if (
      storedDescriptors === null ||
      storedDescriptors.some(
        (stored) =>
          stored.projectId !== descriptor.projectId ||
          stored.requestId !== descriptor.requestId ||
          stored.appId !== descriptor.appId ||
          stored.generationAuthorityAppSpecRef !==
            descriptor.generationAuthorityAppSpecRef ||
          stored.appSpecFingerprint !== descriptor.appSpecFingerprint,
      )
    ) {
      return false
    }
    const existingPointer = readAppGenerationJobActivePointer(storage)
    if (
      existingPointer &&
      !descriptorMatchesActivePointer(descriptor, existingPointer)
    ) {
      return false
    }
    storage.setItem(appGenerationJobStorageKey(descriptor.projectId), encoded)
    writeAppGenerationJobActivePointer(storage, descriptor)
    appGenerationJobDescriptorMemory.set(descriptor.projectId, descriptor)
    return true
  } catch {
    return false
  }
}

function readAppGenerationJobDescriptor(
  projectId: string,
  baseUrl: string,
): AppGenerationJobDescriptor | null {
  const memory = appGenerationJobDescriptorMemory.get(projectId)
  if (memory) {
    const parsed = parseStoredAppGenerationJobDescriptor(
      memory,
      projectId,
      baseUrl,
    )
    if (parsed) return parsed
    appGenerationJobDescriptorMemory.delete(projectId)
  }
  const storage = browserStorage()
  if (!storage) return null
  const key = appGenerationJobStorageKey(projectId)
  try {
    const encoded = storage.getItem(key)
    if (!encoded) return null
    const parsed = parseStoredAppGenerationJobDescriptor(
      JSON.parse(encoded),
      projectId,
      baseUrl,
    )
    if (!parsed) {
      storage.removeItem(key)
      return null
    }
    appGenerationJobDescriptorMemory.set(projectId, parsed)
    return parsed
  } catch {
    storage.removeItem(key)
    return null
  }
}

export function readStoredAppGenerationJob(
  projectId: string,
): AppGenerationJobDescriptor | null {
  return readAppGenerationJobDescriptor(projectId, DEFAULT_BASE_URL)
}

/**
 * Discover the one browser-owned generation subject after a real reload.
 * The global pointer is identity-only; the full descriptor remains
 * project-scoped and self-validating. Ambiguous, orphaned, expired, or stale
 * subjects intentionally produce no auto-resume candidate.
 */
export function readActiveStoredAppGenerationJob(): AppGenerationJobDescriptor | null {
  const storage = browserStorage()
  if (!storage) return null
  const pointer = readAppGenerationJobActivePointer(storage)
  if (!pointer) return null
  const descriptors = readStoredAppGenerationJobCandidates(
    storage,
    DEFAULT_BASE_URL,
  )
  if (descriptors?.length !== 1) {
    storage.removeItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY)
    return null
  }
  const descriptor = descriptors[0]
  if (
    !descriptorMatchesActivePointer(descriptor, pointer) ||
    !activePointerIsFresh(pointer, descriptor)
  ) {
    storage.removeItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY)
    return null
  }
  return descriptor
}

export function clearStoredAppGenerationJob(
  projectId: string,
  jobId?: string,
  requestId?: string,
): void {
  const current = appGenerationJobDescriptorMemory.get(projectId) ??
    readAppGenerationJobDescriptor(projectId, DEFAULT_BASE_URL)
  if (jobId && current?.jobId !== jobId) return
  if (requestId && current?.requestId !== requestId) return
  appGenerationJobDescriptorMemory.delete(projectId)
  appGenerationJobResumeControls.delete(projectId)
  try {
    const storage = browserStorage()
    storage?.removeItem(appGenerationJobStorageKey(projectId))
    if (storage) {
      const pointer = readAppGenerationJobActivePointer(storage)
      if (
        pointer?.projectId === projectId &&
        (!requestId || pointer.requestId === requestId) &&
        (!current || descriptorMatchesActivePointer(current, pointer))
      ) {
        storage.removeItem(APP_GENERATION_JOB_ACTIVE_POINTER_KEY)
      }
    }
  } catch {
    // A storage failure must not turn terminal handling into an app failure.
  }
}

/**
 * G1.4 — three-valued server durability probe consumed by the ghost-job
 * cleanup decision. Produced by project-api's probeProjectGenerationState;
 * declared here (type-only) so the pointer module owns its own vocabulary
 * without a runtime dependency on project-api.
 */
export type AppGenerationJobServerProbe =
  | { outcome: 'missing' }
  | { outcome: 'exists'; generationState: 'pending' | 'complete' | null }
  | { outcome: 'unknown' }

/**
 * G1.4 ghost-job cleanup decision (pure). A stored job pointer must clear
 * instead of retrying when the server says there is nothing to resume:
 *  - the project no longer exists (authoritative 'missing' probe), or
 *  - the server durability marker is already 'complete' — except for a
 *    lifecycle-pending reservation, whose finalization is a separate
 *    idempotent journal transition that legitimately runs AFTER completion.
 * An 'unknown' probe (transient failure) is never proof and never clears —
 * same principle as the existing "only an exact successful project load may
 * invalidate the subject" rule in chat-messages.tsx.
 */
export function shouldClearStoredAppGenerationJobForServerState(
  descriptor: AppGenerationJobDescriptor,
  probe: AppGenerationJobServerProbe,
): boolean {
  if (probe.outcome === 'missing') return true
  if (probe.outcome !== 'exists') return false
  return (
    probe.generationState === 'complete' &&
    descriptor.status !== 'lifecycle-pending'
  )
}

/**
 * Keep the completed server job as a durable project-scoped reservation while
 * Studio finishes the candidate-lifecycle journal transition. Resume
 * capabilities are intentionally discarded: this phase must never poll or
 * restart generation, and it remains valid after the server token expires.
 */
export function markStoredAppGenerationJobLifecyclePending(
  projectId: string,
  jobId?: string,
  requestId?: string,
): boolean {
  const current = readAppGenerationJobDescriptor(projectId, DEFAULT_BASE_URL)
  if (!current?.jobId) return false
  if (jobId && current.jobId !== jobId) return false
  if (requestId && current.requestId !== requestId) return false
  const pending: AppGenerationJobDescriptor = {
    ...current,
    status: 'lifecycle-pending',
  }
  delete pending.executionLane
  delete pending.statusUrl
  delete pending.resumeToken
  delete pending.tokenExpiresAt
  delete pending.terminalDeadlineAt
  delete pending.terminalSloMs
  return persistAppGenerationJobDescriptor(pending)
}

/**
 * Acquire the exact browser recovery subject before a lifecycle-bound direct
 * generation starts. Failure is a pre-generation fail-closed boundary; the
 * completed project must never be left without a reconnect locator.
 */
export function prepareStoredAppGenerationLifecycleReservation(
  appSpecInput: AppSpecFull,
  generationAuthorityAppSpecRef?: string,
): AppGenerationJobDescriptor | null {
  if (
    generationAuthorityAppSpecRef !== undefined &&
    !isAppGenerationFingerprint(generationAuthorityAppSpecRef)
  ) {
    return null
  }
  const appSpec = sanitizeAppSpecForGenerationJob(appSpecInput)
  const projectId = appSpec?.projectId
  const appId = appSpec?.appId
  if (!appSpec || !projectId || !appId) return null

  const appSpecFingerprint = fingerprintAppGenerationSpec(appSpec)
  const current = readAppGenerationJobDescriptor(projectId, DEFAULT_BASE_URL)
  if (current) {
    const exactCreating = current.status === 'creating' &&
      current.appId === appId &&
      current.appSpecFingerprint === appSpecFingerprint &&
      requestedGenerationAuthorityRefMatches(
        current,
        generationAuthorityAppSpecRef,
      )
    if (!exactCreating) return null
    if (persistAppGenerationJobDescriptor(current)) return current
    // The in-memory cache is not a reconnect authority. If durable storage can
    // no longer accept the exact descriptor, fail the preflight and discard
    // the cache entry so a later read cannot masquerade as reload-safe state.
    appGenerationJobDescriptorMemory.delete(projectId)
    return null
  }
  const reservation: AppGenerationJobDescriptor = {
    schemaVersion: APP_GENERATION_JOB_DESCRIPTOR_VERSION,
    requestId: createGenerationRequestId(),
    projectId,
    appId,
    status: 'creating',
    ...(generationAuthorityAppSpecRef
      ? { generationAuthorityAppSpecRef }
      : {}),
    appSpecFingerprint,
    appSpec,
    startedAt: Date.now(),
  }
  return persistAppGenerationJobDescriptor(reservation) ? reservation : null
}

/**
 * Reserve lifecycle-only finalization after a direct SSE generation has
 * already been persisted by the project APIs. Unlike a Platform background
 * job, this descriptor has no resumable generation capability: reconnect may
 * only reopen the exact project and advance its persisted candidate journal.
 */
export function reserveStoredAppGenerationLifecyclePending(
  appSpecInput: AppSpecFull,
  generationAuthorityAppSpecRef?: string,
): AppGenerationJobDescriptor | null {
  if (
    generationAuthorityAppSpecRef !== undefined &&
    !isAppGenerationFingerprint(generationAuthorityAppSpecRef)
  ) {
    return null
  }
  const appSpec = sanitizeAppSpecForGenerationJob(appSpecInput)
  const projectId = appSpec?.projectId
  const appId = appSpec?.appId
  if (!appSpec || !projectId || !appId) return null

  const appSpecFingerprint = fingerprintAppGenerationSpec(appSpec)
  const current = readAppGenerationJobDescriptor(projectId, DEFAULT_BASE_URL)
  if (current) {
    if (
      current.appId !== appId ||
      current.appSpecFingerprint !== appSpecFingerprint ||
      !requestedGenerationAuthorityRefMatches(
        current,
        generationAuthorityAppSpecRef,
      )
    ) {
      return null
    }
    if (current.status === 'lifecycle-pending') return current
    const pending: AppGenerationJobDescriptor = {
      ...current,
      status: 'lifecycle-pending',
    }
    delete pending.executionLane
    delete pending.statusUrl
    delete pending.resumeToken
    delete pending.tokenExpiresAt
    delete pending.terminalDeadlineAt
    delete pending.terminalSloMs
    return persistAppGenerationJobDescriptor(pending) ? pending : null
  }

  const pending: AppGenerationJobDescriptor = {
    schemaVersion: APP_GENERATION_JOB_DESCRIPTOR_VERSION,
    requestId: createGenerationRequestId(),
    projectId,
    appId,
    status: 'lifecycle-pending',
    ...(generationAuthorityAppSpecRef
      ? { generationAuthorityAppSpecRef }
      : {}),
    appSpecFingerprint,
    appSpec,
    startedAt: Date.now(),
  }
  return persistAppGenerationJobDescriptor(pending) ? pending : null
}

export function isDirectAppGenerationLifecycleReservation(
  descriptor: AppGenerationJobDescriptor,
): boolean {
  return descriptor.status === 'creating' &&
    !descriptor.jobId &&
    (descriptor.executionLane === 'direct-fallback' ||
      !shouldUseAppGenerationJob(descriptor.appSpec))
}

function markStoredAppGenerationJobDirectFallback(
  projectId: string,
  requestId: string,
): boolean {
  const current = readAppGenerationJobDescriptor(projectId, DEFAULT_BASE_URL)
  if (
    !current ||
    current.status !== 'creating' ||
    current.jobId ||
    current.requestId !== requestId
  ) {
    return false
  }
  return persistAppGenerationJobDescriptor({
    ...current,
    executionLane: 'direct-fallback',
  })
}

export function resumeAppGenerationJobPolling(projectId: string): boolean {
  const resume = appGenerationJobResumeControls.get(projectId)
  if (!resume) return false
  appGenerationJobResumeControls.delete(projectId)
  resume()
  return true
}

function createGenerationRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `generation-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

interface AppGenerationJobCreateResponse {
  schemaVersion: 'cogo.app-generation-job-receipt.v1'
  requestId: string
  jobId: string
  projectId: string
  appId: string
  status: 'queued' | 'running' | 'complete' | 'failed' | 'cancelled'
  statusUrl: string
  resumeToken: string
  tokenExpiresAt: string
  terminalDeadlineAt: string
  terminalSloMs: number
  appFingerprint: string
  output?: Record<string, unknown>
  error?: unknown
  stage?: string
}

interface AppGenerationJobStatusResponse {
  schemaVersion: 'cogo.app-generation-job-status.v1'
  requestId: string
  jobId: string
  projectId: string
  appId: string
  status: 'queued' | 'running' | 'complete' | 'failed' | 'cancelled'
  progress?: number
  stage?: string
  output?: Record<string, unknown>
  error?: unknown
  appFingerprint: string
  terminalDeadlineAt: string
}

const APP_GENERATION_JOB_FAILURE_IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._-]{0,95}$/

function readAppGenerationJobFailureIdentifier(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim()
  return APP_GENERATION_JOB_FAILURE_IDENTIFIER.test(normalized)
    ? normalized
    : undefined
}

function normalizeAppGenerationJobFailure(
  error: unknown,
  status: 'failed' | 'cancelled',
  stage: unknown,
): { error: string; code: string; stage?: string } {
  const errorRecord = asRecord(error)
  const code =
    readAppGenerationJobFailureIdentifier(errorRecord.code) ??
    (status === 'cancelled'
      ? 'APP_GENERATION_JOB_CANCELLED'
      : 'APP_GENERATION_JOB_FAILED')
  const normalizedStage =
    readAppGenerationJobFailureIdentifier(errorRecord.stage) ??
    readAppGenerationJobFailureIdentifier(stage)
  const message =
    typeof error === 'string' && error.trim()
      ? error.trim()
      : typeof errorRecord.message === 'string' && errorRecord.message.trim()
        ? errorRecord.message.trim()
        : status === 'cancelled'
          ? 'App generation was cancelled by the server.'
          : code

  return {
    error: message,
    code,
    ...(normalizedStage ? { stage: normalizedStage } : {}),
  }
}

class AppGenerationJobUnsupportedLaneError extends Error {
  constructor() {
    super('The app generation lane requires the existing SSE endpoint.')
    this.name = 'AppGenerationJobUnsupportedLaneError'
  }
}

class AppGenerationJobStatusReadAbortedError extends Error {
  constructor() {
    super('App generation job status read was stopped by the caller.')
    this.name = 'AppGenerationJobStatusReadAbortedError'
  }
}

class AppGenerationJobStatusDeadlineError extends Error {
  constructor() {
    super('App generation exceeded the Platform terminal SLO.')
    this.name = 'AppGenerationJobStatusDeadlineError'
  }
}

class AppGenerationJobStatusTransientError extends Error {
  readonly code = 'APP_GENERATION_JOB_STATUS_READ_TRANSIENT'

  constructor() {
    super('App generation status is temporarily unavailable. Resume to retry.')
    this.name = 'AppGenerationJobStatusTransientError'
  }
}

export class AppGenerationJobActiveConflictError extends Error {
  readonly code = 'APP_GENERATION_JOB_ACTIVE_CONFLICT'

  constructor() {
    super(
      'Another app generation is already active for this project. Resume its updates before starting a different request.',
    )
    this.name = 'AppGenerationJobActiveConflictError'
  }
}

export class BlockApiService {
  private baseUrl: string

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? DEFAULT_BASE_URL
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const accessToken = await getStudioAccessToken()
    const res = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: withCogoDevProxyHeaderRecord({
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      }),
      body: JSON.stringify(body),
    })

    const json = await res.json()

    if (!res.ok) {
      throw new Error(
        (json as Record<string, string>).error ?? `API returned ${res.status}`,
      )
    }

    return json as T
  }

  /**
   * Validate a block bundle (14-stage, synchronous, <2s).
   * Degrades to partial local structural checks when the API is unreachable —
   * the result is then labeled source: 'local-fallback' / grade: 'partial'.
   */
  async validateBlock(
    bundle: BlockBundle,
    siblingBlocks?: Record<string, BlockBundle>,
  ): Promise<BlockValidateResponse> {
    const payload = {
      bundle: toServerBlockBundle(bundle),
      ...(siblingBlocks
        ? {
            siblingBlocks: Object.fromEntries(
              Object.entries(siblingBlocks).map(([name, sibling]) => [
                name,
                toServerBlockBundle(sibling),
              ]),
            ),
          }
        : {}),
    }

    try {
      const result = await this.post<BlockValidateResponse>(
        '/api/block/validate',
        payload,
      )
      return { ...result, source: 'server' }
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err)
      console.warn(
        `[BlockApiService] POST /api/block/validate failed (${reason}) — degrading to partial local validation`,
      )
      return this.localValidateBlock(bundle, reason)
    }
  }

  /**
   * Degraded offline fallback: 3 structural checks only (ui.root present,
   * root id/type, events-without-flows). It can surface real structural
   * errors, but it can NEVER establish a pass — when nothing trips it reports
   * grade 'partial' with score 0 instead of fabricating a server-grade
   * pass/100. Issues use stage 0 (not a real server stage number).
   */
  private localValidateBlock(
    bundle: BlockBundle,
    fallbackReason: string,
  ): BlockValidateResponse {
    const errors: string[] = []
    const warnings: string[] = []
    const issues: BlockValidationIssue[] = []

    const root = (bundle.ui as any)?.root
    if (!root) {
      errors.push('local check: ui.root is missing')
      issues.push({
        stage: 0,
        level: 'error',
        message: 'local check: ui.root is missing',
      })
    } else {
      // Basic structural checks
      if (!root.id) {
        warnings.push('local check: root element missing id')
        issues.push({
          stage: 0,
          level: 'warning',
          message: 'local check: root element missing id',
        })
      }
      if (!root.type) {
        errors.push('local check: root element missing type')
        issues.push({
          stage: 0,
          level: 'error',
          message: 'local check: root element missing type',
        })
      }
    }

    const hasEvents = Object.keys(bundle.events ?? {}).length > 0
    const hasFlows = Object.keys(bundle.flow ?? {}).length > 0

    // Event→flow binding check
    if (hasEvents && !hasFlows) {
      warnings.push('local check: events defined but no flows found')
      issues.push({
        stage: 0,
        level: 'warning',
        message: 'local check: events defined but no flows found',
      })
    }

    return {
      // Partial checks cannot establish validity — fail closed.
      valid: false,
      score: 0,
      grade: errors.length > 0 ? 'reject' : 'partial',
      errors,
      warnings,
      issues,
      source: 'local-fallback',
      fallbackReason,
    }
  }

  /**
   * Generate a complete 6-file block bundle from a spec (LLM, synchronous, ~10-30s).
   */
  async generateBlock(
    request: BlockGenerateRequest,
  ): Promise<BlockGenerateResponse> {
    return this.post<BlockGenerateResponse>('/api/block/generate', request)
  }

  /**
   * Fix validation issues via LLM self-correction (synchronous, ~5-10s).
   * Shares the validate endpoint's wire contract — the server 400s unless the
   * bundle is in {@link ServerBlockBundle} shape.
   * @param promptFeedback Optional validation feedback string to inject into LLM prompt
   */
  async fixBlock(
    bundle: BlockBundle,
    issues: BlockValidationIssue[],
    promptFeedback?: string,
  ): Promise<BlockFixResponse> {
    const res = await this.post<BlockFixResponse>('/api/block/fix', {
      bundle: toServerBlockBundle(bundle),
      issues,
      promptFeedback,
    })
    // A successful fix returns a CANONICAL-shaped fixedBundle, and its
    // newValidation carries no `score` (the server returns newScore as a
    // sibling field). Normalize back to the v1 shape + scored result that
    // every consumer expects.
    return {
      ...res,
      ...(res.fixedBundle
        ? {
            fixedBundle: fromServerBlockBundle(
              res.fixedBundle as unknown as Record<string, unknown>,
            ),
          }
        : {}),
      ...(res.newValidation
        ? {
            newValidation: {
              ...res.newValidation,
              score: res.newValidation.score ?? res.newScore ?? 0,
              source: 'server' as const,
            },
          }
        : {}),
    }
  }

  // ─── v2 API Methods ───────────────────────────────────────────

  /**
   * Generate a block via v2 pipeline (2-phase LLM + auto-fix, ~10-30s).
   */
  async generateBlockV2(
    request: BlockGenerateRequest,
  ): Promise<BlockGenerateV2Response> {
    return this.post<BlockGenerateV2Response>('/api/v2/block/generate', request)
  }

  /**
   * Generate a full app. Ordinary generation uses a durable Platform job and
   * normalizes status readback into the existing SSE event contract. Template,
   * deterministic ontology, and composition lanes retain their exact SSE path.
   */
  async *generateApp(
    appSpec: AppSpecFull,
    signal?: AbortSignal,
    generationAuthorityAppSpecRef?: string,
    applicationOntologyGenerationBinding?: ApplicationOntologyGenerationBinding,
    chatGeneratedApplicationDraftPreparation?: ChatGeneratedApplicationDraftPreparationReceipt,
  ): AsyncGenerator<AppGenerateEvent> {
    const serverAppSpec = sanitizeAppSpecForGenerationJob(appSpec)
    if (!serverAppSpec) {
      throw new Error('App generation spec is not serializable.')
    }
    if (
      shouldUseAppGenerationJob(serverAppSpec) &&
      serverAppSpec.projectId &&
      serverAppSpec.appId
    ) {
      const prepared = this.prepareAppGenerationJobDescriptor(
        serverAppSpec,
        generationAuthorityAppSpecRef,
        chatGeneratedApplicationDraftPreparation,
      )
      if (prepared) {
        try {
          for await (const event of this.generateAppWithJob(
            serverAppSpec,
            prepared,
            signal,
            applicationOntologyGenerationBinding,
          )) {
            yield event
          }
          return
        } catch (error) {
          if (!(error instanceof AppGenerationJobUnsupportedLaneError)) {
            throw error
          }
          // Stable fail-safe boundary: only the Platform's explicit
          // unsupported-lane response may fall back to SSE. The job request
          // path itself classifies retryable versus definitive failures.
          if (
            !markStoredAppGenerationJobDirectFallback(
              prepared.projectId,
              prepared.requestId,
            )
          ) {
            throw new Error(
              'Could not persist the direct generation recovery subject.',
            )
          }
        }
      }
    }

    // A BOUND generation has no fallback lane at all — not the ineligible-lane
    // path, not the undurable-descriptor path, not the Platform's explicit
    // unsupported-lane path. The SSE route carries no binding (platform reads
    // it on the job-create body and nowhere else), so reaching here bound
    // would generate this exact application OUTSIDE the binding it was
    // admitted under: the forbidden fallback, not a degraded mode.
    assertNoApplicationOntologyGenerationFallback(
      applicationOntologyGenerationBinding,
    )
    for await (const event of this.generateAppWithSse(serverAppSpec, signal)) {
      yield event
    }
  }

  /**
   * Generic Application Ontology Program — post-program wiring lane A1,
   * seam 1.
   *
   * `applicationOntologyGenerationBinding` is read by the platform job route
   * off the TOP LEVEL of this body — `routes/app-generation-jobs.ts` reads
   * `body.applicationOntologyGenerationBinding`, parses it, pins it onto the
   * reservation and the attempt authority, and passes it to the workflow as
   * `AppGenerationParams.applicationOntologyGenerationBinding`
   * (PLATFORM_CORE_SHA b71e5427). It is therefore spelled here exactly as that
   * reader spells it, at the level that reader reads.
   */
  private appGenerationRequestBody(
    appSpec: AppSpecFull,
    applicationOntologyGenerationBinding?: ApplicationOntologyGenerationBinding,
  ) {
    const templateBound = Boolean(
      appSpec.templateEnforcement || appSpec.templateGenerationModelPolicy,
    )
    return {
      appSpec,
      ...(applicationOntologyGenerationBinding
        ? { applicationOntologyGenerationBinding }
        : {}),
      deterministic: appSpec.deterministic === true,
      boundedModelReview: REQUIRED_APPLICATION_MODEL_REVIEW,
      ...(templateBound ? { mode: 'template-bound-required' } : {}),
      generationMode: appSpec.generationEvidence?.uiOntology.mode,
      uiOntologyPreflight: appSpec.generationEvidence?.uiOntology.preflight,
      runtimeTargets: appSpec.runtimeTargets,
      projectPlan: appSpec.projectPlan,
      adaptiveRuntime: appSpec.adaptiveRuntime,
      goldenSkuId: appSpec.goldenSkuId,
      goldenRecipePack: appSpec.goldenRecipePack,
      // The generator reads design tokens from the TOP-LEVEL `designSystem`
      // key (cogo-cloudflare routes/app-v2.ts:1262 copies it onto its own
      // appSpec, and lib/block-generator-v2.ts:1249 renders it into the block
      // prompt). Forwarded only when a binding set it — every preset and pack
      // generation leaves `appSpec.designSystem` undefined and therefore keeps
      // exactly the request body it had before.
      ...(appSpec.designSystem && Object.keys(appSpec.designSystem).length > 0
        ? { designSystem: appSpec.designSystem }
        : {}),
      ...(appSpec.generationTargetPlatform
        ? { targetPlatform: appSpec.generationTargetPlatform }
        : {}),
    }
  }

  private prepareAppGenerationJobDescriptor(
    appSpec: AppSpecFull,
    generationAuthorityAppSpecRef?: string,
    chatGeneratedApplicationDraftPreparation?: ChatGeneratedApplicationDraftPreparationReceipt,
  ): AppGenerationJobDescriptor | null {
    if (
      generationAuthorityAppSpecRef !== undefined &&
      !isAppGenerationFingerprint(generationAuthorityAppSpecRef)
    ) {
      return null
    }
    const projectId = appSpec.projectId
    const appId = appSpec.appId
    if (!projectId || !appId) return null
    if (
      chatGeneratedApplicationDraftPreparation &&
      chatGeneratedApplicationDraftPreparation.subject.projectId !== projectId
    ) {
      return null
    }
    const appSpecFingerprint = fingerprintAppGenerationSpec(appSpec)
    const stored = readAppGenerationJobDescriptor(projectId, this.baseUrl)
    if (stored?.status === 'lifecycle-pending') {
      throw new AppGenerationJobActiveConflictError()
    }
    if (
      stored &&
      stored.appId === appId &&
      stored.appSpecFingerprint === appSpecFingerprint &&
      stored.chatGeneratedApplicationDraftPreparation?.receiptRef.contentHash ===
        chatGeneratedApplicationDraftPreparation?.receiptRef.contentHash &&
      requestedGenerationAuthorityRefMatches(
        stored,
        generationAuthorityAppSpecRef,
      )
    ) {
      return stored
    }
    if (stored) throw new AppGenerationJobActiveConflictError()
    const descriptor: AppGenerationJobDescriptor = {
      schemaVersion: APP_GENERATION_JOB_DESCRIPTOR_VERSION,
      requestId: createGenerationRequestId(),
      projectId,
      appId,
      status: 'creating',
      ...(generationAuthorityAppSpecRef
        ? { generationAuthorityAppSpecRef }
        : {}),
      ...(chatGeneratedApplicationDraftPreparation
        ? { chatGeneratedApplicationDraftPreparation }
        : {}),
      appSpecFingerprint,
      appSpec,
      startedAt: Date.now(),
    }
    // A durable descriptor is a prerequisite for the async lane. If storage
    // is unavailable or the exact spec exceeds the bounded cache, keep the
    // established SSE path instead of creating an unresumable server job.
    return persistAppGenerationJobDescriptor(descriptor) ? descriptor : null
  }

  private async *generateAppWithJob(
    appSpec: AppSpecFull,
    initialDescriptor: AppGenerationJobDescriptor,
    signal?: AbortSignal,
    applicationOntologyGenerationBinding?: ApplicationOntologyGenerationBinding,
  ): AsyncGenerator<AppGenerateEvent> {
    let descriptor = initialDescriptor
    const resumedStoredJob = descriptor.status === 'running'
    let createReplay: AppGenerationJobCreateResponse | null = null
    if (descriptor.status === 'creating') {
      const accessToken = await getStudioAccessToken()
      const res = await fetch(`${this.baseUrl}/api/v2/app/generation-jobs`, {
        method: 'POST',
        headers: withCogoDevProxyHeaderRecord({
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        }),
        body: JSON.stringify({
          ...this.appGenerationRequestBody(
            appSpec,
            applicationOntologyGenerationBinding,
          ),
          requestId: descriptor.requestId,
        }),
      })
      const json = await res
        .json()
        .catch(() => ({ error: `HTTP ${res.status}` }))
      if (!res.ok) {
        if (
          (json as Record<string, unknown>).code ===
          'app-generation-job-unsupported-lane'
        ) {
          throw new AppGenerationJobUnsupportedLaneError()
        }
        if (
          res.status >= 400 &&
          res.status < 500 &&
          ![401, 403, 408, 429].includes(res.status)
        ) {
          // Definitive request rejection means no resumable workflow exists.
          // Remove only this exact pre-create descriptor so a corrected spec
          // can be submitted; ambiguous network/auth/rate-limit/server errors
          // retain the same requestId for safe idempotent replay.
          clearStoredAppGenerationJob(
            descriptor.projectId,
            undefined,
            descriptor.requestId,
          )
        }
        throw new Error(
          (json as Record<string, string>).error ?? `API returned ${res.status}`,
        )
      }
      const created = json as AppGenerationJobCreateResponse
      if (
        created.schemaVersion !== 'cogo.app-generation-job-receipt.v1' ||
        created.requestId !== descriptor.requestId ||
        typeof created.jobId !== 'string' ||
        !created.jobId ||
        created.projectId !== descriptor.projectId ||
        created.appId !== descriptor.appId ||
        !['queued', 'running', 'complete', 'failed', 'cancelled'].includes(
          created.status,
        ) ||
        typeof created.statusUrl !== 'string' ||
        typeof created.resumeToken !== 'string' ||
        !created.resumeToken ||
        typeof created.tokenExpiresAt !== 'string' ||
        !Number.isFinite(Date.parse(created.tokenExpiresAt)) ||
        Date.parse(created.tokenExpiresAt) <= Date.now() ||
        typeof created.terminalDeadlineAt !== 'string' ||
        !Number.isFinite(Date.parse(created.terminalDeadlineAt)) ||
        Date.parse(created.terminalDeadlineAt) <= Date.now() ||
        typeof created.appFingerprint !== 'string' ||
        !created.appFingerprint ||
        typeof created.terminalSloMs !== 'number' ||
        !Number.isFinite(created.terminalSloMs) ||
        created.terminalSloMs <= 0
      ) {
        throw new Error('Platform returned an invalid app generation job descriptor.')
      }
      descriptor = {
        ...descriptor,
        jobId: created.jobId,
        status: 'running',
        statusUrl: normalizeJobStatusUrl(this.baseUrl, created.statusUrl),
        resumeToken: created.resumeToken,
        tokenExpiresAt: created.tokenExpiresAt,
        terminalDeadlineAt: created.terminalDeadlineAt,
        serverAppFingerprint: created.appFingerprint,
        terminalSloMs: created.terminalSloMs,
      }
      // The pre-POST creating descriptor remains a safe idempotency fallback
      // if this second write fails: refresh retries the same requestId.
      persistAppGenerationJobDescriptor(descriptor)
      createReplay = created
    }

    if (createReplay?.status === 'complete') {
      yield {
        event: 'complete',
        data: {
          ...(createReplay.output ?? {}),
          backgroundJob: {
            ...this.backgroundJobProgress(
              descriptor,
              'generating',
              createReplay.stage ?? 'complete',
              false,
            ),
            terminalStatus: 'complete',
          },
        },
      }
      return
    }
    if (
      createReplay?.status === 'failed' ||
      createReplay?.status === 'cancelled'
    ) {
      const failure = normalizeAppGenerationJobFailure(
        createReplay.error,
        createReplay.status,
        createReplay.stage,
      )
      yield {
        event: 'error',
        data: {
          ...failure,
          backgroundJob: {
            ...this.backgroundJobProgress(
              descriptor,
              'generating',
              failure.stage ?? createReplay.status,
              false,
            ),
            terminalStatus: createReplay.status,
          },
        },
      }
      return
    }

    const initialStatus: AppGenerationBackgroundJobProgress['status'] =
      resumedStoredJob ? 'reconnecting' : 'queued'
    yield {
      event: 'progress',
      data: {
        stage: resumedStoredJob
          ? 'generation-job-reconnecting'
          : 'generation-job-queued',
        message: resumedStoredJob
          ? 'Reconnecting to app generation'
          : 'App generation queued',
        progress: 0,
        backgroundJob: this.backgroundJobProgress(
          descriptor,
          initialStatus,
          resumedStoredJob ? 'reconnecting' : 'queued',
          false,
        ),
      },
    }

    let pollingPaused = signal?.aborted === true
    let pollingPauseStage = 'polling-stopped'
    let pollingPauseMessage = 'Generation continues in the background'
    let wakeDelay: (() => void) | null = null
    const pausePolling = () => {
      pollingPaused = true
      pollingPauseStage = 'polling-stopped'
      pollingPauseMessage = 'Generation continues in the background'
      wakeDelay?.()
    }
    signal?.addEventListener('abort', pausePolling, { once: true })
    let pollAttempt = 0
    let lastProgressSignature = ''
    let lastProgressEmissionAt = Date.now()
    try {
      while (true) {
        if (pollingPaused) {
          const resumePolling = new Promise<void>((resolve) => {
            appGenerationJobResumeControls.set(descriptor.projectId, resolve)
          })
          yield {
            event: 'progress',
            data: {
              stage: `generation-job-${pollingPauseStage}`,
              message: pollingPauseMessage,
              progress: 0,
              backgroundJob: this.backgroundJobProgress(
                descriptor,
                'paused',
                pollingPauseStage,
                true,
              ),
            },
          }
          await resumePolling
          pollingPaused = false
          pollingPauseStage = 'polling-stopped'
          pollingPauseMessage = 'Generation continues in the background'
          pollAttempt = 0
          yield {
            event: 'progress',
            data: {
              stage: 'generation-job-reconnecting',
              message: 'Reconnecting to app generation',
              progress: 0,
              backgroundJob: this.backgroundJobProgress(
                descriptor,
                'reconnecting',
                'reconnecting',
                false,
              ),
            },
          }
        }

        let status: AppGenerationJobStatusResponse
        try {
          status = await this.readAppGenerationJobStatus(
            descriptor,
            signal?.aborted ? undefined : signal,
          )
        } catch (error) {
          if (error instanceof AppGenerationJobStatusReadAbortedError) {
            // The server workflow continues. Re-enter the paused branch and
            // keep the exact descriptor so Resume can reconnect safely.
            pollingPaused = true
            continue
          }
          if (error instanceof AppGenerationJobStatusDeadlineError) {
            yield {
              event: 'error',
              data: {
                error: error.message,
                code: 'APP_GENERATION_JOB_TERMINAL_SLO_EXCEEDED',
                stage: 'terminal-slo-exceeded',
                backgroundJob: this.backgroundJobProgress(
                  descriptor,
                  'reconnecting',
                  'terminal-slo-exceeded',
                  true,
                ),
              },
            }
            return
          }
          if (error instanceof AppGenerationJobStatusTransientError) {
            // A bounded read timeout or transport-side AbortError is not a
            // generation failure. Keep the descriptor and expose the same
            // explicit Resume affordance as a caller-paused poll.
            pollingPaused = true
            pollingPauseStage = 'status-read-timeout'
            pollingPauseMessage = error.message
            continue
          }
          throw error
        }
        if (status.status === 'complete') {
          yield {
            event: 'complete',
            data: {
              ...(status.output ?? {}),
              backgroundJob: {
                ...this.backgroundJobProgress(
                  descriptor,
                  'generating',
                  'complete',
                  false,
                ),
                terminalStatus: 'complete',
              },
            },
          }
          return
        }
        if (status.status === 'failed' || status.status === 'cancelled') {
          const failure = normalizeAppGenerationJobFailure(
            status.error,
            status.status,
            status.stage,
          )
          yield {
            event: 'error',
            data: {
              ...failure,
              backgroundJob: {
                ...this.backgroundJobProgress(
                  descriptor,
                  'generating',
                  failure.stage ?? status.status,
                  false,
                ),
                terminalStatus: status.status,
              },
            },
          }
          return
        }

        // A long browser pause may outlive the server deadline even though the
        // workflow completed. Read authoritative status first; only a still
        // queued/running job can violate the exact server-owned deadline.
        if (
          descriptor.terminalDeadlineAt &&
          Date.now() > Date.parse(descriptor.terminalDeadlineAt)
        ) {
          yield {
            event: 'error',
            data: {
              error: 'App generation exceeded the Platform terminal SLO.',
              code: 'APP_GENERATION_JOB_TERMINAL_SLO_EXCEEDED',
              stage: 'terminal-slo-exceeded',
              backgroundJob: this.backgroundJobProgress(
                descriptor,
                'reconnecting',
                'terminal-slo-exceeded',
                true,
              ),
            },
          }
          return
        }

        const normalizedProgress =
          typeof status.progress === 'number' && Number.isFinite(status.progress)
            ? Math.max(
                0,
                Math.min(100, status.progress <= 1 ? status.progress * 100 : status.progress),
              )
            : 0
        const stage = status.stage ?? `generation-job-${status.status}`
        const signature = `${status.status}:${stage}:${normalizedProgress}`
        const now = Date.now()
        const isHeartbeat =
          signature === lastProgressSignature &&
          now - lastProgressEmissionAt >= 15_000
        if (signature !== lastProgressSignature || isHeartbeat) {
          yield {
            event: 'progress',
            data: {
              stage: isHeartbeat ? 'block-generation-heartbeat' : stage,
              jobStage: stage,
              message:
                status.status === 'queued'
                  ? 'App generation queued'
                  : 'Generating app in the background',
              progress: normalizedProgress,
              backgroundJob: this.backgroundJobProgress(
                descriptor,
                status.status === 'queued' ? 'queued' : 'generating',
                stage,
                false,
              ),
            },
          }
          lastProgressSignature = signature
          lastProgressEmissionAt = now
        }

        pollAttempt += 1
        const delayMs = Math.min(
          APP_GENERATION_JOB_MAX_POLL_MS,
          APP_GENERATION_JOB_MIN_POLL_MS + (pollAttempt - 1) * 750,
        )
        await new Promise<void>((resolve) => {
          const timer = setTimeout(() => {
            wakeDelay = null
            resolve()
          }, delayMs)
          wakeDelay = () => {
            clearTimeout(timer)
            wakeDelay = null
            resolve()
          }
        })
      }
    } finally {
      signal?.removeEventListener('abort', pausePolling)
      const pendingWakeDelay = wakeDelay as (() => void) | null
      pendingWakeDelay?.()
      appGenerationJobResumeControls.delete(descriptor.projectId)
    }
  }

  private backgroundJobProgress(
    descriptor: AppGenerationJobDescriptor,
    status: AppGenerationBackgroundJobProgress['status'],
    stage: string,
    canResume: boolean,
  ): AppGenerationBackgroundJobProgress {
    return {
      schemaVersion: 'studio-app-generation-background-progress.v1',
      projectId: descriptor.projectId,
      ...(descriptor.jobId ? { jobId: descriptor.jobId } : {}),
      status,
      stage,
      canResume,
      continuesInBackground: true,
      ...(descriptor.terminalSloMs
        ? { terminalSloMs: descriptor.terminalSloMs }
        : {}),
    }
  }

  private async readAppGenerationJobStatus(
    descriptor: AppGenerationJobDescriptor,
    callerSignal?: AbortSignal,
  ): Promise<AppGenerationJobStatusResponse> {
    if (!descriptor.statusUrl || !descriptor.resumeToken || !descriptor.jobId) {
      throw new Error('Stored app generation job is missing resume authority.')
    }
    const terminalDeadline = Date.parse(descriptor.terminalDeadlineAt ?? '')
    const remainingTerminalMs = terminalDeadline - Date.now()
    if (!Number.isFinite(remainingTerminalMs) || remainingTerminalMs <= 0) {
      throw new AppGenerationJobStatusDeadlineError()
    }
    const deadlineBound =
      remainingTerminalMs <= APP_GENERATION_JOB_STATUS_READ_TIMEOUT_MS
    const readTimeoutMs = Math.min(
      APP_GENERATION_JOB_STATUS_READ_TIMEOUT_MS,
      remainingTerminalMs,
    )
    const readController = new AbortController()
    let timedOut = false
    const stopRead = () => readController.abort()
    if (callerSignal?.aborted) stopRead()
    else callerSignal?.addEventListener('abort', stopRead, { once: true })
    const timeout = setTimeout(() => {
      timedOut = true
      readController.abort()
    }, readTimeoutMs)
    let onReadAbort: (() => void) | undefined
    const readAborted = new Promise<never>((_resolve, reject) => {
      onReadAbort = () => reject(new DOMException('Aborted', 'AbortError'))
      if (readController.signal.aborted) onReadAbort()
      else {
        readController.signal.addEventListener('abort', onReadAbort, {
          once: true,
        })
      }
    })
    let res: Response
    let json: unknown
    try {
      const accessToken = await Promise.race([
        getStudioAccessToken(),
        readAborted,
      ])
      res = await fetch(
        normalizeJobStatusUrl(this.baseUrl, descriptor.statusUrl),
        {
          method: 'GET',
          headers: withCogoDevProxyHeaderRecord({
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            'X-Cogo-Generation-Job-Token': descriptor.resumeToken,
          }),
          signal: readController.signal,
        },
      )
      try {
        json = await res.json()
      } catch (error) {
        if (readController.signal.aborted) throw error
        json = { error: `HTTP ${res.status}` }
      }
    } catch (error) {
      if (callerSignal?.aborted) {
        throw new AppGenerationJobStatusReadAbortedError()
      }
      if (timedOut && deadlineBound) {
        throw new AppGenerationJobStatusDeadlineError()
      }
      if (
        timedOut ||
        (error instanceof DOMException && error.name === 'AbortError') ||
        (error instanceof Error && error.name === 'AbortError')
      ) {
        throw new AppGenerationJobStatusTransientError()
      }
      throw error
    } finally {
      clearTimeout(timeout)
      callerSignal?.removeEventListener('abort', stopRead)
      if (onReadAbort) {
        readController.signal.removeEventListener('abort', onReadAbort)
      }
    }
    if (!res.ok) {
      throw new Error(
        (json as Record<string, string>).error ?? `API returned ${res.status}`,
      )
    }
    const status = json as AppGenerationJobStatusResponse
    if (
      status.schemaVersion !== 'cogo.app-generation-job-status.v1' ||
      status.requestId !== descriptor.requestId ||
      status.jobId !== descriptor.jobId ||
      status.projectId !== descriptor.projectId ||
      status.appId !== descriptor.appId ||
      status.appFingerprint !== descriptor.serverAppFingerprint ||
      status.terminalDeadlineAt !== descriptor.terminalDeadlineAt ||
      !['queued', 'running', 'complete', 'failed', 'cancelled'].includes(
        status.status,
      )
    ) {
      throw new Error('Platform returned a mismatched app generation job status.')
    }
    return status
  }

  private async *generateAppWithSse(
    appSpec: AppSpecFull,
    signal?: AbortSignal,
  ): AsyncGenerator<AppGenerateEvent> {
    const accessToken = await getStudioAccessToken()
    const res = await fetch(`${this.baseUrl}/api/v2/app/generate`, {
      method: 'POST',
      headers: withCogoDevProxyHeaderRecord({
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      }),
      body: JSON.stringify(this.appGenerationRequestBody(appSpec)),
      signal,
    })

    if (!res.ok) {
      const json = await res
        .json()
        .catch(() => ({ error: `HTTP ${res.status}` }))
      throw new Error(
        (json as Record<string, string>).error ?? `API returned ${res.status}`,
      )
    }

    const reader = res.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      // SSE messages are separated by double newline
      const messages = buffer.split('\n\n')
      buffer = messages.pop() ?? ''

      for (const msg of messages) {
        if (!msg.trim()) continue
        let eventName = ''
        let dataStr = ''
        for (const line of msg.split('\n')) {
          if (line.startsWith('event: ')) {
            eventName = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            dataStr += line.slice(6)
          }
        }
        if (eventName && dataStr) {
          try {
            const data = JSON.parse(dataStr)
            yield { event: eventName, data }
          } catch {
            console.warn(
              `[generateApp] Failed to parse SSE data for event "${eventName}":`,
              dataStr.slice(0, 200),
            )
          }
        }
      }
    }
  }

  /**
   * Runs the platform ActionFlow test chunk proof against generated app
   * evidence. This is the Studio-side client boundary for the agentic
   * ActionFlow generation-validation route; the server remains responsible for
   * KG retrieval, optional LLM candidate generation, deterministic mapping,
   * runtime trace execution, oracle checks, and promotion recording.
   */
  async verifyAgenticActionFlowTestChunks(
    request: AgenticActionFlowTestChunkProofRequest,
    options: { clientSessionId?: string; signal?: AbortSignal } = {},
  ): Promise<AgenticActionFlowTestChunkProofResponse> {
    const headers: Record<string, string> = withCogoDevProxyHeaderRecord({
      'Content-Type': 'application/json',
    })
    if (options.clientSessionId)
      headers['X-Cogo-Client-Session'] = options.clientSessionId
    const res = await fetch(
      `${this.baseUrl}/api/agentic/actionflow-test-chunks`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: options.signal,
      },
    )
    const json = await res.json()
    if (!res.ok) {
      const record =
        json && typeof json === 'object'
          ? (json as Record<string, unknown>)
          : {}
      const error =
        typeof record.error === 'string'
          ? record.error
          : Array.isArray(record.blockedReasons)
            ? record.blockedReasons.join('; ')
            : `API returned ${res.status}`
      throw new Error(error)
    }
    return json as AgenticActionFlowTestChunkProofResponse
  }

  /**
   * Validate all blocks in an app with cross-block checks.
   */
  async validateApp(
    blocks: Record<string, Record<string, unknown>>,
    globalState?: Record<string, unknown>,
  ): Promise<AppValidateResponse> {
    return this.post<AppValidateResponse>('/api/v2/app/validate', {
      blocks,
      globalState,
    })
  }

  /**
   * Generate → Validate → Fix pipeline.
   * Returns the best bundle after generation and optional auto-fix.
   */
  async generateAndValidate(
    request: BlockGenerateRequest,
    autoFix = true,
  ): Promise<{
    bundle: BlockBundle | null
    validation: BlockValidateResponse | null
    fixed: boolean
  }> {
    const genResult = await this.generateBlock(request)
    if (!genResult.ok || !genResult.block) {
      return { bundle: null, validation: null, fixed: false }
    }

    const bundle = genResult.block as unknown as BlockBundle
    const validation = genResult.validationResult ?? null

    // If score is good enough or autoFix disabled, return as-is
    if (!autoFix || !validation || validation.grade === 'pass') {
      return { bundle, validation, fixed: false }
    }

    // Attempt auto-fix
    const errorIssues = validation.issues.filter(
      (i) => i.level === 'error' || i.level === 'warning',
    )
    if (errorIssues.length === 0) {
      return { bundle, validation, fixed: false }
    }

    try {
      const promptFeedback =
        generatePromptFeedbackFromBundle(bundle) || undefined
      const fixResult = await this.fixBlock(bundle, errorIssues, promptFeedback)
      if (fixResult.ok && fixResult.fixedBundle) {
        return {
          bundle: fixResult.fixedBundle,
          validation: fixResult.newValidation ?? validation,
          fixed: true,
        }
      }
    } catch {
      // Fix failed — return original
    }

    return { bundle, validation, fixed: false }
  }
}

// ─── V2 BlockDef Types ────────────────────────────────────────

export interface BlockBundleV2 {
  meta: {
    name: string
    schemaVersion: string
    blockType: string
    [k: string]: unknown
  }
  ui: { root: Record<string, unknown> }
  events: Record<string, unknown>
  flow: Record<string, unknown>
  state: {
    variables: Record<string, unknown>
    computed?: Record<string, unknown>
  }
  data: Record<string, unknown>
}

/**
 * Promote `options.className` to top-level `className` on each UiElement node recursively.
 * v2 renderer expects className at the node level, not nested in options.
 */
function promoteClassName(node: any): any {
  if (!node || typeof node !== 'object') return node
  const result = { ...node }
  if (result.options?.className && !result.className) {
    result.className = result.options.className
  }
  if (Array.isArray(result.children)) {
    result.children = result.children.map(promoteClassName)
  }
  return result
}

/**
 * Convert a v1 BlockBundle (from Cloudflare API) to v2 BlockBundleV2 (for LoadedApp.blocks).
 */
export function toV2Bundle(
  bundle: BlockBundle,
  blockName: string,
): BlockBundleV2 {
  const blockMeta = bundle.block as Record<string, unknown>

  // Resolve root from various formats
  const ui = bundle.ui as any
  const rawRoot = ui?.root ?? (Array.isArray(ui?.tree) ? ui.tree[0] : ui)
  const root = promoteClassName(rawRoot)
  const variables = (bundle.variables ?? {}) as Record<string, unknown>
  const scopedVariables =
    variables.localState ||
    variables.state ||
    variables.props ||
    variables.computed
      ? variables
      : { localState: variables }

  return {
    meta: {
      name: blockName,
      schemaVersion: '3.0',
      blockType:
        (blockMeta?.blockType as string) ?? (blockMeta?.type as string) ?? 'ui',
      ...blockMeta,
    },
    ui: { root },
    events: bundle.events ?? {},
    flow: bundle.flow ?? {},
    state: {
      ...scopedVariables,
      variables,
      computed:
        (scopedVariables.computed as Record<string, unknown> | undefined) ?? {},
    },
    data: bundle.datasource ?? {},
  }
}

/** Singleton instance for convenience */
export const blockApi = new BlockApiService()
